import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronDown, Download, Printer, CheckCheck, Loader2, Clock, AlertTriangle,
  Check, X, Car, RotateCcw, FileText, ClipboardCheck, Smile, Activity, Lock, CalendarDays, FileSpreadsheet,
  Coins, MoonStar,
} from 'lucide-react';
import api from '../../utils/api';
import AutoTravelModal from './AutoTravelModal';
import XeroPayrollTab from './XeroPayrollTab';
import ContractorBillsTab from './ContractorBillsTab';
import EmploymentHeroPayrollTab from './EmploymentHeroPayrollTab';
import QuickBooksPayrollTab from './QuickBooksPayrollTab';
import PayLinesDialog, { extraMinutesOf, extraStatusOf } from './PayLinesDialog';
import { usePayEngine } from '../Settings/payRatesKit';
import usePayPermissions, { needsPermission } from '../../hooks/usePayPermissions';
import useConnectedIntegrations from '../../hooks/useConnectedIntegrations';
import toast from 'react-hot-toast';
import { format, addDays, parseISO, startOfWeek } from 'date-fns';
import clsx from 'clsx';
import { fmtTime12, isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  KpiTile, Tabs, useUrlTab, Dialog, BulkBar, OverflowMenu, EmptyState, ErrorState, TableSkeleton,
  PersonAvatar, StatusPill, SearchSelect, money, num, plural,
} from '../../components/ui';
import { Notice, Segmented, codeLabel } from '../Financials/financeKit';

// Columns that only earn their space on very wide screens. The note is still one click away in
// the row menu, so hiding it below 2xl costs nothing.
const WIDE_ONLY = 'hidden 2xl:table-cell';

const warnIcon = <AlertTriangle size={16} className="text-amber-500" />;

// Pay Rates: an approved timesheet on a company paid from Pay Rates has pay lines to show, and a
// sleepover signed out late has extra minutes waiting for a decision. A timesheet not approved yet
// opens there too, for its km (spec 6.5: an admin can change the km before approval, with a reason).
const hasPayLines = (ts, ratesEngine) => (ratesEngine && ts.status !== 'rejected') || extraMinutesOf(ts) > 0;
// Km figures differ when they are a hundredth of a km apart or more (the server's rule).
const kmDiffers = (a, b) => Math.round((Number(a) || 0) * 100) !== Math.round((Number(b) || 0) * 100);
function LateSignOutBadge({ ts, onOpen }) {
  if (extraMinutesOf(ts) <= 0 || ['approved', 'rejected'].includes(extraStatusOf(ts))) return null;
  return (
    <button type="button" onClick={onOpen} title="Late sign-out after a sleepover: approve or reject the extra time"
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800 hover:bg-violet-200">
      <MoonStar size={10} aria-hidden="true" />Late sign-out
    </button>
  );
}

// NDIS/SCHADS service names are long and dash-delimited, e.g.
// "Assistance in Supported Independent Living - High Intensity - Weekday Daytime".
// Render them compactly: a primary service label (noisy "Assistance in/with" prefix
// trimmed) plus small chips for the level and the day+time band. The full original string
// is preserved in the tooltip so nothing is lost. Stored codes ('community_access') are
// written as words.
// "15:12" → "3:12 pm".
const t12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).slice(0, 5).split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
};

function splitService(value) {
  const raw = String(value || 'Standard').trim();
  // Split only on a spaced " - " delimiter so hyphenated words (e.g. "Self-Care") stay intact.
  const parts = raw.split(/\s+[-–—]\s+/).map(s => s.trim()).filter(Boolean);
  const service = codeLabel((parts[0] || raw).replace(/^assistance\s+(in|with)\s+/i, ''));
  return { raw, service, tags: parts.slice(1) };
}

function ShiftTypeCell({ value }) {
  const { raw, service, tags } = splitService(value);
  return (
    <div className="min-w-[150px] max-w-[190px]" title={raw}>
      <p className="text-xs font-medium text-slate-700 leading-tight truncate">{service}</p>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {tags.map((t, i) => (
            <span key={i} className="px-1.5 py-0.5 rounded-md text-[10px] font-medium leading-none whitespace-nowrap bg-slate-100 text-slate-600 ring-1 ring-slate-200/70">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// One-line version for the list: the service, the first band as a chip, and a count for the rest.
function ShiftTypeInline({ value }) {
  const { raw, service, tags } = splitService(value);
  return (
    <div className="flex items-center gap-1.5 min-w-0 max-w-[260px]" title={raw}>
      <span className="truncate text-[13px] text-slate-700">{service}</span>
      {tags[0] && <span className="shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-medium leading-none whitespace-nowrap bg-slate-100 text-slate-600">{tags[0]}</span>}
      {tags.length > 1 && <span className="shrink-0 text-[11px] text-slate-500">+{tags.length - 1}</span>}
    </div>
  );
}

// Compact split of a long NDIS service name into its primary label + the
// day/time band chip, for the roster cards. Mirrors ShiftTypeCell's parsing.
function parseService(value) {
  const raw = String(value || 'Standard').trim();
  const parts = raw.split(/\s+[-–—]\s+/).map(s => s.trim()).filter(Boolean);
  const service = codeLabel((parts[0] || raw).replace(/^assistance\s+(in|with)\s+/i, ''));
  const band = parts.slice(1).find(p =>
    /public holiday|sunday|saturday|weekday|evening|night|sleepover|daytime|overnight|morning|afternoon/i.test(p)
  ) || parts[1] || '';
  return { service, band };
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Safe date formatter: date-fns `format` THROWS "Invalid time value" when given an
// invalid Date. A simple truthiness guard (`x ? format(new Date(x), …) : '—'`) is
// not enough because `new Date(<unparseable>)` is an Invalid Date — truthy but
// invalid — so it slips past the guard and crashes the whole render. Use this for
// every value coming from the API that could be null/blank/unparseable.
function fmtDate(value, pattern, fallback = '—') {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? fallback : format(d, pattern);
}

// Same guard but for date-only strings parsed via parseISO (e.g. 'YYYY-MM-DD').
function fmtISO(value, pattern, fallback = '—') {
  if (value == null || value === '') return fallback;
  try {
    const d = parseISO(typeof value === 'string' ? value.slice(0, 10) : value);
    return isNaN(d.getTime()) ? fallback : format(d, pattern);
  } catch {
    return fallback;
  }
}

// 'Sat 19 Sep' this year, 'Sat 19 Sep 2025' otherwise.
function shortDate(value) {
  const s = value == null ? '' : String(value).slice(0, 10);
  const y = Number(s.slice(0, 4));
  return fmtISO(s, y && y !== new Date().getFullYear() ? 'EEE d MMM yyyy' : 'EEE d MMM');
}

const BANDS = [
  { key: 'wd_early',       label: '12a–6a' },
  { key: 'wd_day',         label: '6a–8p' },
  { key: 'wd_evening',     label: '8p–10p' },
  { key: 'wd_night',       label: '10p–12a' },
  { key: 'saturday',       label: 'Sat' },
  { key: 'sunday',         label: 'Sun' },
  { key: 'public_holiday', label: 'PH' },
];

// Colour a note_type badge by its kind (best-effort; falls back to slate).
function noteTypeBadgeCls(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('incident')) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (t.includes('behaviour') || t.includes('behavior')) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

// Billing problems come back from the approval routes as plain reasons: `billing.errors` (an array)
// or, when the whole billing step threw, `billing.error`.
const billingErrors = (b) => {
  if (!b) return [];
  if (Array.isArray(b.errors)) return b.errors.filter(Boolean).map(String);
  return b.error ? [String(b.error)] : [];
};

// A note for the page banner. Repeated reasons are grouped, so three shifts short of transport
// funding read as one line, not three.
function billingNote(billing, context) {
  const list = billingErrors(billing);
  if (!list.length) return null;
  const grouped = {};
  for (const r of list) grouped[r] = (grouped[r] || 0) + 1;
  return { context, reasons: Object.entries(grouped).map(([reason, n]) => ({ reason, n })) };
}

// ─────────────────────────────────────────────────────────────────────────────
// NOTE DETAIL — full view of every progress note attached to one timesheet.
// Mirrors the mobile app: note_type badge, author + time, full content, mood /
// behaviour ratings, activities completed, and attachment photo thumbnails.
// All optional fields are guarded — notes may omit any of them.
// ─────────────────────────────────────────────────────────────────────────────
function NoteDetailModal({ row, onClose }) {
  const [lightbox, setLightbox] = useState(null);   // attachment data-URL shown full size
  const notes = Array.isArray(row?.notes) ? row.notes : [];
  const title = [row?.staff_name, fmtISO(row?.shift_date_str, 'EEE d MMM yyyy', '')]
    .filter(Boolean).join(' · ') || 'Progress notes';

  // The photo viewer closes on Escape by itself; the dialog behind it stays open.
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setLightbox(null); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [lightbox]);

  return (
    <Dialog open onClose={onClose} size="lg" dismissable={!lightbox}
      title="Progress notes"
      description={`${title}${notes.length ? ` · ${plural(notes.length, 'note')}` : ''}`}
      footer={<button type="button" onClick={onClose} className="btn-secondary">Close</button>}>
      <div className="space-y-3">
        {row?._loading && <TableSkeleton rows={3} cols={2} />}
        {!row?._loading && notes.length === 0 && (
          row?.progress_note
            ? <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{row.progress_note}</p>
            : <p className="text-center py-10 text-sm text-slate-500">No notes for this shift.</p>
        )}
        {notes.map((n, i) => (
          <div key={n?.id || i} className="border border-slate-200 rounded-xl p-3.5">
            {/* Type + author + time */}
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="flex items-center gap-2">
                <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border', noteTypeBadgeCls(n?.note_type))}>
                  {codeLabel(n?.note_type) || 'Note'}
                </span>
                {n?.is_private && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                    <Lock size={10} /> Private
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-500">
                {n?.author ? `${n.author} · ` : ''}{fmtDate(n?.created_at, 'd MMM yyyy, h:mm aaa', '')}
              </span>
            </div>

            {/* Content */}
            {n?.note_content
              ? <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{n.note_content}</p>
              : <p className="text-xs text-slate-500 italic">No content</p>}

            {/* Ratings */}
            {(n?.mood_rating != null || n?.behaviour_rating != null) && (
              <div className="flex items-center gap-2 flex-wrap mt-2.5">
                {n?.mood_rating != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-700 border border-slate-200">
                    <Smile size={11} /> Mood {n.mood_rating}/5
                  </span>
                )}
                {n?.behaviour_rating != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-slate-50 text-slate-700 border border-slate-200">
                    <Activity size={11} /> Behaviour {n.behaviour_rating}/5
                  </span>
                )}
              </div>
            )}

            {/* Activities */}
            {Array.isArray(n?.activities_completed) && n.activities_completed.length > 0 && (
              <div className="mt-2.5">
                <p className="text-xs font-medium text-slate-500 mb-1">Activities completed</p>
                <ul className="flex flex-wrap gap-1.5">
                  {n.activities_completed.map((a, ai) => (
                    <li key={ai} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] bg-white border border-slate-200 text-slate-600">
                      <Check size={10} className="text-emerald-600" /> {typeof a === 'string' ? a : (a?.name || a?.label || JSON.stringify(a))}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Attachment photo thumbnails */}
            {Array.isArray(n?.attachments) && n.attachments.length > 0 && (
              <div className="mt-2.5">
                <p className="text-xs font-medium text-slate-500 mb-1">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {n.attachments.map((a, ai) => (
                    typeof a === 'string' && a
                      ? <button key={ai} type="button" onClick={() => setLightbox(a)} aria-label={`Open attachment ${ai + 1}`}
                          className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-white hover:ring-2 hover:ring-primary-300 transition-shadow duration-150">
                          <img src={a} alt={`Attachment ${ai + 1}`} className="w-full h-full object-cover" />
                        </button>
                      : null
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Attachment viewer — above the dialog */}
      {lightbox && createPortal(
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-6 bg-black/70" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Attachment, full size" className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
          <button type="button" onClick={() => setLightbox(null)} aria-label="Close attachment"
            className="absolute top-4 right-4 p-2 rounded-full bg-white/15 hover:bg-white/25 text-white"><X size={20} /></button>
        </div>,
        document.body,
      )}
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BULK APPROVE MODAL — Brevity-style review grid for the selected timesheets.
// Read each worker's notes and approve km + time together. Inline-edit start/end
// time and kilometres; claim company-fleet km (Used Company Car → no km paid).
// ─────────────────────────────────────────────────────────────────────────────
function BulkApproveModal({ ids, onClose, onDone }) {
  const { can } = usePayPermissions();
  const canApprove = can('timesheets.approve');
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});   // id -> { approve, in, out, breakMin, km, companyCar }
  const [saving, setSaving] = useState(false);
  const [noteModalRow, setNoteModalRow] = useState(null);   // row whose full notes are shown

  useEffect(() => {
    api.get(`/timesheets/review-batch?ids=${ids.join(',')}`)
      .then(r => {
        const data = r.data || [];
        setRows(data);
        const d = {};
        for (const t of data) {
          d[t.id] = {
            approve: true,
            in:  t.clock_in_hm  || t.sched_start_hm || '09:00',
            out: t.clock_out_hm || t.sched_end_hm   || '17:00',
            breakMin: Number(t.break_minutes) || 0,
            km: Number(t.mileage_km) || Number(t.trip_km) || 0,
            // The figure shown first. On Pay Rates, changing it needs a reason (logged).
            kmOriginal: Number(t.mileage_km) || Number(t.trip_km) || 0,
            kmReason: '',
            // The KM decision. On unless this timesheet was approved before with its KM held,
            // so approving keeps paying and charging KM exactly as it always has; untick to hold.
            approveKm: t.km_approved !== false,
            companyCar: !!t.company_vehicle,
            modifiedVehicle: !!t.modified_vehicle,
          };
        }
        setDraft(d);
      })
      .catch((e) => { toast.error(e.response?.data?.message || 'Failed to load timesheets'); onClose(); });
  }, [ids, onClose]);

  const upd = (id, patch) => setDraft(d => ({ ...d, [id]: { ...d[id], ...patch } }));

  // Hours from edited wall-clock times (handles overnight: out <= in → +1 day).
  const calcHours = (r) => {
    if (!r?.in || !r?.out) return null;
    const [ih, im] = r.in.split(':').map(Number);
    const [oh, om] = r.out.split(':').map(Number);
    let mins = (oh * 60 + om) - (ih * 60 + im);
    if (mins <= 0) mins += 24 * 60;
    return Math.max(0, mins / 60 - (Number(r.breakMin) || 0) / 60);
  };

  // Reset puts the TIMES back to the booked hours and nothing else. It used to zero the kilometres
  // as well, which silently threw away the km the worker recorded — a travel claim wiped by a click
  // meant only to undo a time edit.
  const resetRow = (t) => upd(t.id, {
    in: t.sched_start_hm || '09:00', out: t.sched_end_hm || '17:00', breakMin: 0,
  });

  const setAllApprove = (v) => setDraft(d => {
    const n = { ...d };
    for (const k of Object.keys(n)) n[k] = { ...n[k], approve: v };
    return n;
  });
  const setAllApproveKm = (v) => setDraft(d => {
    const n = { ...d };
    for (const k of Object.keys(n)) n[k] = { ...n[k], approveKm: v };
    return n;
  });
  const setAllCompanyCar = (v) => setDraft(d => {
    const n = { ...d };
    for (const k of Object.keys(n)) n[k] = { ...n[k], companyCar: v };
    return n;
  });
  const resetAll = () => setDraft(d => {
    const n = { ...d };
    for (const t of (rows || [])) {
      n[t.id] = { ...n[t.id], in: t.sched_start_hm || '09:00', out: t.sched_end_hm || '17:00', breakMin: 0 };
    }
    return n;
  });

  const approveCount = Object.values(draft).filter(r => r.approve).length;

  // Pay Rates shifts whose km were changed here: each needs a reason before approving.
  const needsKmReason = (t) => {
    const r = draft[t.id];
    return !!(t.pay_rates && r && r.approve && r.approveKm && kmDiffers(r.km, r.kmOriginal));
  };

  const submit = async () => {
    const missing = (rows || []).filter((t) => needsKmReason(t) && String(draft[t.id].kmReason || '').trim().length < 3);
    if (missing.length) {
      toast.error(missing.length === 1 ? 'Give a reason for the km you changed.' : `Give a reason for the km you changed on ${missing.length} timesheets.`);
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map(t => {
        const r = draft[t.id];
        const date = t.shift_date_str || (t.shift_date || '').slice(0, 10);
        // Build naive wall-clock timestamps; overnight → clock-out next day.
        const nextDay = r.out <= r.in;
        let outDateStr = date;
        if (nextDay) { const dd = new Date(date); dd.setDate(dd.getDate() + 1); outDateStr = dd.toISOString().slice(0, 10); }
        return {
          id: t.id,
          approve: !!r.approve,
          clockIn:  `${date}T${r.in}:00`,
          clockOut: `${outDateStr}T${r.out}:00`,
          breakMinutes: Number(r.breakMin) || 0,
          mileageKm: Number(r.km) || 0,
          approveKm: !!r.approveKm,
          ...(needsKmReason(t) ? { kmReason: String(r.kmReason || '').trim() } : {}),
          usedCompanyCar: !!r.companyCar,
          modifiedVehicle: !!r.modifiedVehicle,
        };
      });
      const { data } = await api.post('/timesheets/bulk-approve-adjust', { rows: payload });
      // Surface real reasons: per-row approval failures here, billing problems on the page.
      const rowReason = data.failures?.[0]?.error;
      if (data.failed > 0 && data.approved === 0) {
        toast.error(`Failed to approve${rowReason ? `: ${rowReason}` : ''}`);
        setSaving(false);
        return;
      }
      if (data.failed > 0) toast(`${data.message}${rowReason ? ` — reason: ${rowReason}` : ''}`, { icon: warnIcon, duration: 8000 });
      else if (billingErrors(data.billing).length) toast(`${data.message || 'Approved'}. Billing needs a look — see the note on the page.`, { icon: warnIcon, duration: 6000 });
      else toast.success(data.message || 'Approved');
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to approve');
      setSaving(false);
    }
  };

  const bulkBtn = 'inline-flex items-center gap-1 min-h-[32px] px-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-slate-900/40">
      <div role="dialog" aria-modal="true" aria-labelledby="bulk-approve-title"
        className="bg-white rounded-2xl shadow-modal w-full max-w-[1280px] max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div>
            <h2 id="bulk-approve-title" className="text-[17px] font-semibold text-slate-900 flex items-center gap-2">
              <ClipboardCheck size={17} className="text-slate-500" /> Approve timesheets
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">Read each worker's notes, adjust time and kilometres, then approve them all at once.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        {/* Bulk controls */}
        <div className="px-6 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap text-xs shrink-0 bg-slate-50/60">
          <span className="text-slate-600 font-medium mr-1">Set every row:</span>
          <button type="button" onClick={() => setAllApprove(true)} className={bulkBtn}><Check size={12} /> Approve all</button>
          <button type="button" onClick={() => setAllApprove(false)} className={bulkBtn}>Approve none</button>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <button type="button" onClick={() => setAllCompanyCar(true)} className={bulkBtn}><Car size={12} /> All company car</button>
          <button type="button" onClick={() => setAllCompanyCar(false)} className={bulkBtn}>No company car</button>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <button type="button" onClick={() => setAllApproveKm(true)} title="Approve the KM on every row: paid to the worker, charged to the client" className={bulkBtn}><Check size={12} /> Approve all KM</button>
          <button type="button" onClick={() => setAllApproveKm(false)} title="Hold the KM on every row: approve the hours only" className={bulkBtn}>Hold KM</button>
          <span className="text-slate-300" aria-hidden="true">·</span>
          <button type="button" onClick={resetAll} title="Reset every row's actual time back to its booked/scheduled hours. Kilometres are kept." className={bulkBtn}><RotateCcw size={12} /> Reset all to booked</button>
        </div>

        {/* Grid */}
        <div className="overflow-auto flex-1">
          {!rows && <TableSkeleton rows={5} cols={7} />}
          {rows && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-slate-200 text-xs font-semibold text-slate-500">
                  <th scope="col" className="px-3 py-2.5 text-left w-16">Approve</th>
                  <th scope="col" className="px-3 py-2.5 text-left">Date and client</th>
                  <th scope="col" className="px-3 py-2.5 text-left min-w-[160px]">Service</th>
                  <th scope="col" className="px-3 py-2.5 text-center">Booked</th>
                  <th scope="col" className="px-3 py-2.5 text-center">Actual (edit)</th>
                  <th scope="col" className="px-3 py-2.5 text-right">Hours</th>
                  <th scope="col" className="px-3 py-2.5 text-center" title="Tick Approve KM to pay the worker and charge the client for these kilometres">KM and approve</th>
                  <th scope="col" className="px-3 py-2.5 text-center">Company car</th>
                  <th scope="col" className="px-3 py-2.5 text-center">Modified vehicle</th>
                  <th scope="col" className="px-3 py-2.5 text-left min-w-[200px]">Notes</th>
                  <th scope="col" className="px-3 py-2.5 text-center w-20">Reset</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(t => {
                  const r = draft[t.id] || {};
                  const hrs = calcHours(r);
                  const changed = r.in !== t.sched_start_hm || r.out !== t.sched_end_hm;
                  const noteArr = Array.isArray(t.notes) ? t.notes : [];
                  const noteText = noteArr.map(n => n?.note_content).filter(Boolean).join('\n\n') || t.progress_note || '';
                  const who = `${t.staff_name || 'timesheet'}, ${fmtISO(t.shift_date_str, 'EEE d MMM')}`;
                  return (
                    <tr key={t.id} className={clsx('align-top', !r.approve && 'opacity-50')}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={!!r.approve} onChange={e => upd(t.id, { approve: e.target.checked })}
                          aria-label={`Approve ${who}`} className="rounded w-4 h-4" />
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-semibold text-slate-800 text-xs whitespace-nowrap">{fmtISO(t.shift_date_str, 'EEE d MMM')}</p>
                        <p className="text-slate-600 text-xs">{t.staff_name}</p>
                        <p className="text-slate-500 text-[11px] truncate max-w-[150px]">{t.client_names || '—'}</p>
                        {!t.clock_out_hm && (
                          <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-none ring-1 bg-rose-50 text-rose-800 ring-rose-200"
                            title="The worker clocked in but never clocked out. The finish time below is the rostered finish: check it before approving.">
                            No clock-out
                          </span>
                        )}
                        {t.auto_clocked_out && (
                          <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-none ring-1 bg-violet-50 text-violet-800 ring-violet-200"
                            title={t.auto_clock_out_basis === 'gps' ? 'The worker did not clock out. Rostery clocked them out when GPS showed them leaving the client. Check the finish time.'
                              : t.auto_clock_out_basis === 'cap' ? 'The worker did not clock out and the shift has no usable finish time. Rostery closed it 16 hours after clock-in. Check the finish time.'
                              : 'The worker did not clock out. Rostery clocked them out at the rostered finish. Check the finish time.'}>
                            Auto clocked out
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 min-w-[160px] max-w-[200px]">
                        <ShiftTypeCell value={t.shift_type} />
                        {t.is_sil && (
                          <span className={`inline-block mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-none ring-1 ${t.weekly_covered ? 'bg-teal-50 text-teal-800 ring-teal-200' : 'bg-violet-50 text-violet-700 ring-violet-200'}`}
                            title={t.weekly_covered ? 'SIL shift covered by the weekly claim: pay counts as SIL cost, nothing is billed per shift' : 'SIL shift: pay counts as SIL cost'}>
                            {t.weekly_covered ? 'SIL weekly' : 'SIL'}
                          </span>
                        )}
                        {(t.roster_comments || t.activity) && <p className="text-[11px] text-slate-500 mt-1 max-w-[180px] truncate" title={t.roster_comments || t.activity}>{t.roster_comments || t.activity}</p>}
                      </td>
                      <td className="px-3 py-3 text-center whitespace-nowrap">
                        <span className="tabular-nums text-xs text-slate-600">{t.sched_start_hm || '—'}–{t.sched_end_hm || '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-center whitespace-nowrap">
                        {/* 118px, not 84: in a 12-hour locale the field reads '03:00 pm' plus the clock
                            button, and at 84px the am/pm was cut off, so 3:00 pm read as '03:00'. */}
                        <div className="inline-flex items-center gap-1">
                          <input type="time" value={r.in || ''} onChange={e => upd(t.id, { in: e.target.value })}
                            aria-label={`Actual start, ${who}`}
                            className={clsx('w-[118px] text-xs border rounded-lg px-1.5 py-1 tabular-nums', changed ? 'border-rose-300 text-rose-700' : 'border-slate-200 text-slate-700')} />
                          <span className="text-slate-400" aria-hidden="true">–</span>
                          <input type="time" value={r.out || ''} onChange={e => upd(t.id, { out: e.target.value })}
                            aria-label={`Actual finish, ${who}`}
                            className={clsx('w-[118px] text-xs border rounded-lg px-1.5 py-1 tabular-nums', changed ? 'border-rose-300 text-rose-700' : 'border-slate-200 text-slate-700')} />
                        </div>
                        {t.auto_clocked_out && t.suggested_out_hm && r.out !== t.suggested_out_hm && (
                          <button type="button" onClick={() => upd(t.id, { out: t.suggested_out_hm })}
                            className="mt-1 text-[11px] font-semibold text-violet-700 hover:underline">
                            GPS: left {t12(t.suggested_out_hm)}. Use it
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="font-semibold text-slate-900 text-xs tabular-nums">{hrs != null ? `${hrs.toFixed(2)} h` : '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {(() => {
                          // An approved timesheet's KM is settled (paid and charged). It is shown, not
                          // edited: unapprove the timesheet to change it.
                          const kmLocked = t.status === 'approved';
                          const held = !r.approveKm && Number(r.km) > 0;
                          return (
                            <>
                              <div className="inline-flex items-center gap-1.5">
                                <input type="number" min={0} step="0.1" value={r.km} disabled={kmLocked}
                                  onChange={e => upd(t.id, { km: e.target.value })}
                                  aria-label={`Kilometres, ${who}`}
                                  title={kmLocked ? 'This timesheet is approved. Unapprove it to change the KM.' : undefined}
                                  className={clsx('w-[68px] text-xs border rounded-lg px-1.5 py-1 text-right tabular-nums disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed',
                                    held ? 'border-amber-300 text-slate-500' : 'border-slate-200')} />
                                <button type="button" onClick={() => upd(t.id, { approveKm: !r.approveKm })} disabled={kmLocked}
                                  title={kmLocked ? 'KM already decided. Unapprove the timesheet to change it.' : 'Approve KM'}
                                  aria-label={`Approve KM, ${who}`} aria-pressed={!!r.approveKm}
                                  className={clsx('w-8 h-8 rounded-lg border inline-flex items-center justify-center transition-colors duration-150 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60',
                                    r.approveKm ? 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700' : 'bg-white border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-400')}>
                                  {kmLocked ? <Lock size={12} className={r.approveKm ? 'text-white' : 'text-slate-500'} /> : <Check size={14} strokeWidth={3} />}
                                </button>
                              </div>
                              {Number(t.trip_km) > 0 && <p className="text-[11px] text-slate-500 mt-0.5 tabular-nums">Trips: {Number(t.trip_km).toFixed(1)}</p>}
                              {Number(t.rejected_km) > 0 && <p className="text-[11px] text-rose-600 mt-0.5 tabular-nums" title="Rejected trips are not counted">{Number(t.rejected_km).toFixed(1)} km rejected</p>}
                              {held && <p className="text-[11px] text-amber-700 mt-0.5">Held: not paid or charged</p>}
                              {!kmLocked && needsKmReason(t) && (
                                <input type="text" value={r.kmReason || ''} maxLength={500}
                                  onChange={e => upd(t.id, { kmReason: e.target.value })}
                                  placeholder={`Reason (was ${Number(r.kmOriginal || 0).toFixed(2)} km)`}
                                  aria-label={`Reason for changing the km, ${who}`} aria-required="true"
                                  className={clsx('mt-1 w-[150px] text-[11px] border rounded-lg px-1.5 py-1',
                                    String(r.kmReason || '').trim().length < 3 ? 'border-amber-300 bg-amber-50' : 'border-slate-200')} />
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button type="button" onClick={() => upd(t.id, { companyCar: !r.companyCar })}
                          aria-pressed={!!r.companyCar} aria-label={`Company car, ${who}`}
                          className={clsx('min-h-[32px] px-2.5 rounded-lg text-[11px] font-semibold border inline-flex items-center gap-1',
                            r.companyCar ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-600')}>
                          <Car size={11} /> {r.companyCar ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <input type="checkbox" checked={!!r.modifiedVehicle}
                          onChange={e => upd(t.id, { modifiedVehicle: e.target.checked })}
                          aria-label={`Modified vehicle, ${who}`}
                          className="rounded w-4 h-4" />
                      </td>
                      <td className="px-3 py-3 min-w-[200px] max-w-[280px]">
                        {noteText ? (
                          <p className="text-[12px] text-slate-600 whitespace-pre-line leading-snug" title={noteText}>
                            {noteText}
                          </p>
                        ) : (
                          <span className="text-[12px] text-slate-500 inline-flex items-center gap-1"><FileText size={11} /> No note</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button type="button" onClick={() => resetRow(t)} title="Reset this row's actual time back to booked/scheduled hours. Kilometres are kept."
                          aria-label={`Reset ${who} to booked hours`}
                          className="inline-flex items-center gap-1 min-h-[32px] px-2 rounded-lg text-[11px] font-semibold text-slate-600 border border-slate-300 bg-white hover:bg-slate-50">
                          <RotateCcw size={11} /> Reset
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-600">{approveCount} of {rows?.length || 0} selected to approve · unticked rows stay pending</p>
            <p className="text-xs text-slate-500 mt-0.5">Company car = client charged, worker not paid for KM. Modified vehicle = higher client rate.</p>
            <p className="text-xs text-slate-500 mt-0.5">Approve KM ticked = KM paid to the worker, charged to the client and approved in Mileage. Unticked = hours approved, KM held.</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">Close</button>
            <button type="button" onClick={submit} disabled={saving || approveCount === 0 || !canApprove} className="btn-primary"
              title={canApprove ? undefined : needsPermission('timesheets.approve')}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
              {approveCount > 0 ? `Approve ${approveCount}` : 'Approve'}
            </button>
          </div>
        </div>
      </div>

      {/* Full progress-note detail (per row) */}
      {noteModalRow && (
        <NoteDetailModal row={noteModalRow} onClose={() => setNoteModalRow(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PENDING APPROVAL TAB
// ─────────────────────────────────────────────────────────────────────────────
// A timesheet is "pending approval" while it hasn't been approved or rejected — this
// covers legacy 'review' rows (manual clock-ins) AND 'pending' rows (bulk-complete), so
// the queue matches the Staff Hours Report and nothing falls through the cracks.
const isPendingTs = (s) => s !== 'approved' && s !== 'rejected';

const STATUS_FILTERS = ['pending', 'approved', 'all'];

// Hours for one shift: prefer actual worked, else the scheduled span (overnight-safe).
const tsHours = (t) => {
  if (t.hours_worked != null && t.hours_worked !== '') return parseFloat(t.hours_worked) || 0;
  const s = t.sched_start_hm || t.scheduled_start, e = t.sched_end_hm || t.scheduled_end;
  if (s && e) {
    const [sh, sm] = String(s).split(':').map(Number);
    const [eh, em] = String(e).split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 1440;
    return Math.max(0, mins / 60);
  }
  return 0;
};

function PendingApprovalTab({ onCountChange, onBillingNote }) {
  const confirm = useConfirm();
  // Timesheets: Approve approves; Timesheets: Edit Approved unapproves (Pay Rates v1.2 section 10).
  const { can } = usePayPermissions();
  const canApprove = can('timesheets.approve');
  const canEditApproved = can('timesheets.edit_approved');
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [processing, setProcessing] = useState({});
  const [statusFilter, setStatusFilterUrl] = useUrlTab('status', 'pending', STATUS_FILTERS);
  const [selected, setSelected] = useState([]);
  const [bulkIds, setBulkIds] = useState(null);       // the ids the review modal was opened with
  const [bulkUnapproving, setBulkUnapproving] = useState(false);
  const [quickApproving, setQuickApproving] = useState(false);
  const [view, setView] = useState('list');           // 'list' | 'calendar'
  const [noteModal, setNoteModal] = useState(null);   // full-notes detail for a row
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [travelModal, setTravelModal] = useState(false);
  const [payLinesTs, setPayLinesTs] = useState(null);  // timesheet whose pay lines are open
  const ratesEngine = usePayEngine() === 'rates';
  // Monday of the week shown in the Calendar (staff × days) grid.
  const [calWeekStart, setCalWeekStart] = useState(() => getMonday(new Date()));
  // Roster grid density — 'comfortable' = rich cards, 'compact' = dense single-line
  // rows so an all-clients sheet (many staff × many shifts) stays scannable.
  // Persisted so the operator's chosen density sticks across sessions.
  const [calDensity, setCalDensity] = useState(() => {
    try { return localStorage.getItem('ts_cal_density') || 'comfortable'; } catch { return 'comfortable'; }
  });
  useEffect(() => { try { localStorage.setItem('ts_cal_density', calDensity); } catch { /* ignore */ } }, [calDensity]);
  // Group the calendar rows by staff (default) or by client. Client mode lists
  // participants down the left, mirroring the roster-by-client view. Persisted.
  const [calGroupBy, setCalGroupBy] = useState(() => {
    try { return localStorage.getItem('ts_cal_groupby') || 'staff'; } catch { return 'staff'; }
  });
  useEffect(() => { try { localStorage.setItem('ts_cal_groupby', calGroupBy); } catch { /* ignore */ } }, [calGroupBy]);
  const groupByClient = calGroupBy === 'client';
  // Collapsed staff rows (by name) — fold away already-audited staff so an
  // all-clients week stays short and scannable. Session-only.
  const [collapsedStaff, setCollapsedStaff] = useState(() => new Set());
  const toggleStaffCollapse = (name) => setCollapsedStaff(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const setStatusFilter = (v) => { setStatusFilterUrl(v); setSelected([]); };

  // Load dropdown sources once.
  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || []))).catch(() => {});
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (clientFilter) params.set('client_id', clientFilter);
    if (staffFilter)  params.set('staff_id', staffFilter);
    const qs = params.toString();
    // The tiles are counted over every status for the same client / staff scope, so the status
    // tab never changes them (it used to zero 'Approved' on the Pending view).
    const sp = new URLSearchParams();
    if (clientFilter) sp.set('client_id', clientFilter);
    if (staffFilter)  sp.set('staff_id', staffFilter);
    api.get(`/timesheets/stats${sp.toString() ? `?${sp}` : ''}`)
      .then(r => { setStats(r.data || null); if (r.data && r.data.pending_count != null) onCountChange?.(r.data.pending_count); })
      .catch(() => setStats(null));
    api.get(`/timesheets${qs ? `?${qs}` : ''}`)
      .then(r => {
        setTimesheets(r.data);
        setSelected([]);
        if (statusFilter === 'pending') onCountChange?.(r.data.length);
      })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load timesheets. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [onCountChange, statusFilter, clientFilter, staffFilter]);

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const pendingIds = () => timesheets.filter(t => isPendingTs(t.status)).map(t => t.id);
  // Rows that can be ticked depend on the active filter: pending rows drive bulk-approve;
  // when viewing Approved, approved rows drive bulk-unapprove instead.
  const isSelectable = (ts) => statusFilter === 'approved' ? ts.status === 'approved' : isPendingTs(ts.status);
  const selectableIds = () => timesheets.filter(isSelectable).map(t => t.id);
  const toggleAll = () => setSelected(s => (selectableIds().length > 0 && s.length === selectableIds().length) ? [] : selectableIds());
  // Select/unselect every pending shift in one calendar row (staff or client).
  const toggleRowSelect = (ids) => setSelected(prev => {
    const allSel = ids.length > 0 && ids.every(id => prev.includes(id));
    return allSel ? prev.filter(id => !ids.includes(id)) : [...new Set([...prev, ...ids])];
  });

  useEffect(() => { load(); }, [load]);

  // The review modal gets the ids it was opened with, held in state, so a re-render of this list
  // can never reload it and throw away edits in progress.
  const openReview = (ids) => setBulkIds(ids && ids.length ? ids : pendingIds());
  const closeReview = useCallback(() => setBulkIds(null), []);
  const onReviewDone = (data) => {
    setBulkIds(null);
    load();
    const note = billingNote(data?.billing, `${plural(data?.approved || 0, 'timesheet')} approved in the review`);
    if (note) onBillingNote?.(note);
  };

  const approve = async (ts) => {
    const id = ts.id;
    // Never clocked out: the finish time is set in Review & approve (it starts at the rostered end).
    if (ts.clock_in_time && !ts.clock_out_time) {
      toast('The worker never clocked out. Check the finish time (it starts at the rostered finish), then approve.', { icon: warnIcon });
      openReview([id]);
      return;
    }
    // Rostery clocked this one out: its times are checked in Review & approve first.
    if (ts.auto_clocked_out && !ts.auto_reviewed_at) {
      toast('Rostery clocked this worker out automatically. Check the finish time, then approve.', { icon: warnIcon });
      openReview([id]);
      return;
    }
    setProcessing(p => ({ ...p, [id]: 'approve' }));
    try {
      const { data } = await api.post(`/timesheets/${id}/approve`);
      const note = billingNote(data?.billing, `${ts.staff_name || 'Timesheet'} · ${shortDate(ts.shift_date)}`);
      if (note) {
        onBillingNote?.(note);
        toast('Timesheet approved. Billing needs a look — see the note on the page.', { icon: warnIcon, duration: 6000 });
      } else toast.success('Timesheet approved');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to approve'); }
    finally { setProcessing(p => { const n = { ...p }; delete n[id]; return n; }); }
  };

  // View the full progress note(s) for a row — fetch the decrypted note objects on demand.
  const viewNotes = async (ts) => {
    setNoteModal({ staff_name: ts.staff_name, shift_date_str: ts.shift_date && String(ts.shift_date).slice(0, 10), notes: [], _loading: true });
    try {
      const r = await api.get(`/timesheets/review-batch?ids=${ts.id}`);
      const row = Array.isArray(r.data) ? r.data[0] : null;
      setNoteModal(row || { staff_name: ts.staff_name, notes: [], progress_note: ts.progress_note });
    } catch {
      setNoteModal({ staff_name: ts.staff_name, notes: [], progress_note: ts.progress_note });
    }
  };

  // Unapprove: send an approved timesheet back to Pending. Backend reverses the
  // invoice + refunds the fund. Replaces the destructive Delete.
  const unapprove = async (ts) => {
    const ok = await confirm('This reverses its invoice and refunds the fund, then moves it back to Pending.', {
      title: `Unapprove ${ts.staff_name ? `${ts.staff_name}'s` : 'this'} timesheet for ${shortDate(ts.shift_date)}?`,
      confirmLabel: 'Unapprove', danger: true,
    });
    if (!ok) return;
    setProcessing(p => ({ ...p, [ts.id]: 'unapprove' }));
    try {
      await api.post(`/timesheets/${ts.id}/unapprove`);
      toast.success('Timesheet moved back to pending');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to unapprove'); }
    finally { setProcessing(p => { const n = { ...p }; delete n[ts.id]; return n; }); }
  };

  // Bulk unapprove: send several approved timesheets back to Pending in one call.
  // Backend reverses each invoice + refunds the fund (POST /timesheets/bulk-unapprove).
  const bulkUnapprove = async () => {
    if (!selected.length) return;
    const n = selected.length;
    const ok = await confirm('This reverses their invoices and refunds the funds, then moves them back to Pending. Any already billed on a sent or paid invoice stay approved.', {
      title: `Unapprove ${plural(n, 'timesheet')}?`, confirmLabel: `Unapprove ${n}`, danger: true,
    });
    if (!ok) return;
    setBulkUnapproving(true);
    try {
      const r = await api.post('/timesheets/bulk-unapprove', { ids: selected });
      toast.success(r.data?.message || `${selected.length} timesheets moved back to pending`);
      setSelected([]);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to unapprove'); }
    finally { setBulkUnapproving(false); }
  };

  // Quick-approve bills every pending timesheet in view in one click, so it says exactly what it
  // is about to do first. The request is unchanged: every pending id in view.
  const bulkApproveAll = async () => {
    const pend = timesheets.filter(t => isPendingTs(t.status));
    const ids = pend.map(t => t.id);
    if (!ids.length) return;
    // The server quick-approves only rows still in Pending status; rows marked Review are left for
    // Review & approve. Say so up front rather than after.
    const eligible = pend.filter(t => t.status == null || t.status === 'pending');
    const reviewOnly = pend.length - eligible.length;
    if (!eligible.length) {
      toast(`${plural(reviewOnly, 'timesheet')} in view ${reviewOnly === 1 ? 'needs' : 'need'} Review & approve; quick approval skips them.`, { icon: warnIcon });
      return;
    }
    const hours = eligible.reduce((s, t) => s + tsHours(t), 0);
    const people = new Set(eligible.map(t => t.staff_id || t.staff_name)).size;
    const lines = [`${num(hours, 1)} hrs across ${plural(people, 'staff member')}. This creates their invoice lines and draws down client funds.`];
    if (reviewOnly) lines.push(`${plural(reviewOnly, 'more timesheet')} marked Review ${reviewOnly === 1 ? 'is' : 'are'} skipped; use Review & approve for ${reviewOnly === 1 ? 'it' : 'them'}.`);
    const ok = await confirm(lines.join('\n\n'), {
      title: `Approve ${plural(eligible.length, 'timesheet')}?`,
      confirmLabel: `Approve ${eligible.length}`,
    });
    if (!ok) return;
    setQuickApproving(true);
    try {
      const { data } = await api.post('/timesheets/bulk-approve', { ids });
      const approved = data?.approved ?? eligible.length;
      const skipped = data?.skipped || 0;
      const note = billingNote(data?.billing, `${plural(approved, 'timesheet')} quick-approved`);
      if (note) onBillingNote?.(note);
      if (skipped || note) {
        toast(`${plural(approved, 'timesheet')} approved${skipped ? `, ${skipped} skipped (not in Pending status)` : ''}${note ? '. Billing needs a look — see the note on the page.' : ''}`,
          { icon: warnIcon, duration: 8000 });
      } else {
        toast.success(`${plural(approved, 'timesheet')} approved`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to bulk approve'); }
    finally { setQuickApproving(false); }
  };

  const pendingCount = timesheets.filter(t => isPendingTs(t.status)).length;

  // ── Calendar (staff × days) grid model ───────────────────────────────────────
  // YYYY-MM-DD key for a timesheet's shift_date.
  const dayKey = (ts) => (ts.shift_date ? ts.shift_date.slice(0, 10) : '');
  // The 7 day columns (Mon→Sun) for the currently-shown calendar week. Build keys
  // from LOCAL date parts (not toISOString, which is UTC and would shift the day in
  // AU timezones) so they line up with the DB's shift_date 'YYYY-MM-DD' strings.
  const calDayCols = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(calWeekStart, i);
    return { date: d, key: fmtDate(d, 'yyyy-MM-dd') };
  });
  // Group timesheets into rows — by staff (default) or by client — then by day key
  // (columns), keeping only those inside the shown week. Rows sorted alphabetically.
  // Client mode keys on the shift's client label so participants list down the left,
  // mirroring the roster-by-client view (a rare multi-client shift stays one row).
  const weekKeys = new Set(calDayCols.map(c => c.key));
  const byRow = {};
  for (const ts of timesheets) {
    const k = dayKey(ts);
    if (!k || !weekKeys.has(k)) continue;
    const name = groupByClient ? (ts.client_names || 'No client') : (ts.staff_name || 'Unknown');
    if (!byRow[name]) byRow[name] = {
      name,
      photo: groupByClient ? null : ts.staff_photo,
      staffId: groupByClient ? null : ts.staff_id,
      days: {},
    };
    (byRow[name].days[k] = byRow[name].days[k] || []).push(ts);
  }
  const calStaffRows = Object.values(byRow).sort((a, b) => a.name.localeCompare(b.name));
  const shiftCalWeek = (n) => setCalWeekStart(prev => addDays(prev, n * 7));
  const calThisWeek = () => setCalWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const statusDot = (s) => s === 'approved' ? 'bg-emerald-500' : s === 'rejected' ? 'bg-red-500' : 'bg-amber-500';
  const statusBorder = (s) => s === 'approved' ? 'border-l-emerald-400' : s === 'rejected' ? 'border-l-red-400' : 'border-l-amber-400';
  const statusMeta = (s) => s === 'approved' ? { label: 'Approved', cls: 'text-emerald-700' }
    : s === 'rejected' ? { label: 'Rejected', cls: 'text-red-600' }
    : { label: 'Pending', cls: 'text-amber-700' };
  // Minutes-since-midnight of a shift's scheduled (or actual) start — orders shifts
  // within a day so handover times read top-to-bottom (staff 1 out, staff 2 in).
  const startMins = (t) => {
    const hm = t.sched_start_hm || t.scheduled_start;
    if (hm) { const [h, m] = String(hm).split(':').map(Number); return (h || 0) * 60 + (m || 0); }
    if (t.clock_in_time) { const d = new Date(t.clock_in_time); return d.getHours() * 60 + d.getMinutes(); }
    return 0;
  };
  const fmtHrs = (h) => (Math.round(h * 100) / 100).toFixed(2).replace(/\.00$/, '');
  // Grand totals across every staff row shown this week — the all-clients audit
  // summary that sits above the grid.
  const grand = calStaffRows.reduce((acc, sr) => {
    const all = Object.values(sr.days).flat();
    acc.hours    += all.reduce((a, t) => a + tsHours(t), 0);
    acc.approved += all.filter(t => t.status === 'approved').reduce((a, t) => a + tsHours(t), 0);
    acc.pending  += all.filter(t => t.status !== 'approved' && t.status !== 'rejected').reduce((a, t) => a + tsHours(t), 0);
    acc.km       += all.reduce((a, t) => a + (parseFloat(t.mileage_km || t.mileage || 0) || 0), 0);
    acc.shifts   += all.length;
    return acc;
  }, { hours: 0, approved: 0, pending: 0, km: 0, shifts: 0 });
  const allStaffNames  = calStaffRows.map(sr => sr.name);
  const everyCollapsed = allStaffNames.length > 0 && allStaffNames.every(n => collapsedStaff.has(n));
  const toggleCollapseAll = () => setCollapsedStaff(everyCollapsed ? new Set() : new Set(allStaffNames));
  // Density-derived layout tokens so the header + rows stay aligned.
  const compact  = calDensity === 'compact';
  const gridCols = compact ? 'grid-cols-[168px_repeat(7,minmax(104px,1fr))]' : 'grid-cols-[220px_repeat(7,minmax(132px,1fr))]';
  const gridMinW = compact ? 'min-w-[896px]' : 'min-w-[1144px]';

  // Summary tiles — from /timesheets/stats (every status, same client/staff scope), so they
  // never contradict the status tab. Until they arrive the tiles show '…'.
  const st = stats || {};
  const hrs = (v) => `${num(Number(v) || 0, 1)} h`;

  const filtered = !!(clientFilter || staffFilter);
  const emptyCopy = statusFilter === 'pending'
    ? { title: filtered ? 'No timesheets waiting for approval for these filters' : 'No timesheets waiting for approval',
        hint: 'A timesheet appears here once the worker clocks out of a shift.' }
    : statusFilter === 'approved'
      ? { title: 'No approved timesheets for these filters', hint: 'Approved timesheets appear here after Review & approve or Approve.' }
      : { title: 'No timesheets for these filters', hint: 'Try another client or staff member.' };

  const clockRange = (ts) => (
    <span className="tabular-nums whitespace-nowrap">
      {fmtDate(ts.clock_in_time, 'h:mm aaa')}–{ts.clock_out_time
        ? fmtDate(ts.clock_out_time, 'h:mm aaa')
        : <span className="text-amber-700 font-medium">not clocked out</span>}
    </span>
  );

  return (
    <div className="space-y-4">
      {/* Summary — counted over every status, so the status tab never changes them */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Pending" accent="amber" value={stats ? num(st.pending_count) : '…'}
          sub={stats ? `${hrs(st.pending_hours)} awaiting review` : null} />
        <KpiTile label="Approved" accent="emerald" value={stats ? num(st.approved_count) : '…'}
          sub={stats ? `${hrs(st.approved_hours)} approved` : null} />
        <KpiTile label="Total hours" accent="slate" value={stats ? hrs(st.total_hours) : '…'}
          sub={stats ? `${plural(st.total_count || 0, 'timesheet')}, all statuses` : null} />
        <KpiTile label="Staff waiting" accent="slate" value={stats ? num(st.pending_staff) : '…'}
          sub={stats ? 'with timesheets to review' : null} />
      </div>

      {/* Action bar */}
      <div className="card p-3 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented ariaLabel="Timesheet status" value={statusFilter} onChange={setStatusFilter} size="sm"
            options={[
              { value: 'pending', label: 'Pending', count: stats ? st.pending_count : undefined },
              { value: 'approved', label: 'Approved', count: stats ? st.approved_count : undefined },
              { value: 'all', label: 'All', count: stats ? st.total_count : undefined },
            ]} />
          {/* Type-to-search pickers: a long staff or client list is a few letters, not a scroll. */}
          <SearchSelect ariaLabel="Client" allLabel="All clients" placeholder="Search clients…"
            value={clientFilter} onChange={setClientFilter}
            options={clients.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name}`.trim() }))
              .sort((a, b) => a.label.localeCompare(b.label))} />
          <SearchSelect ariaLabel="Staff" allLabel="All staff" placeholder="Search staff…"
            value={staffFilter} onChange={setStaffFilter}
            options={staff.map(s => ({ value: s.id, label: `${s.first_name} ${s.last_name}`.trim() }))
              .sort((a, b) => a.label.localeCompare(b.label))} />
          <Segmented ariaLabel="View" value={view} onChange={setView} size="sm"
            options={[{ value: 'list', label: 'List' }, { value: 'calendar', label: 'Calendar' }]} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-slate-600 mr-1">
            <strong className="text-slate-900 tabular-nums">{timesheets.length}</strong> timesheet{timesheets.length !== 1 ? 's' : ''}
          </p>
          {pendingCount > 0 && statusFilter !== 'approved' && (
            <>
              <button type="button" onClick={() => openReview(selected)} disabled={!canApprove} className="btn-primary whitespace-nowrap"
                title={canApprove ? undefined : needsPermission('timesheets.approve')}>
                <ClipboardCheck size={14} /> Review &amp; approve {selected.length > 0 ? selected.length : `${pendingCount} pending`}
              </button>
              <button type="button" onClick={bulkApproveAll} disabled={quickApproving || !canApprove} title={canApprove ? 'Approve every pending timesheet in view without reviewing each' : needsPermission('timesheets.approve')}
                className="btn-secondary whitespace-nowrap">
                {quickApproving ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />} Quick-approve all
              </button>
            </>
          )}
          {/* Travel km is billable and was typed in by hand. Sits next to approval because
              that is when someone is looking at the day and can still correct it. */}
          <button type="button" onClick={() => setTravelModal(true)} title="Work out travel kilometres from the addresses on file"
            className="btn-secondary whitespace-nowrap">
            <Car size={14} /> Calculate travel
          </button>
        </div>
      </div>

      {loadError && (
        <div className="card"><ErrorState message={loadError} onRetry={load} /></div>
      )}

      {/* LIST VIEW */}
      {!loadError && view === 'list' && (
        <div className="card overflow-hidden">
          {loading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : timesheets.length === 0 ? (
            <EmptyState icon={CheckCheck} title={emptyCopy.title} hint={emptyCopy.hint} />
          ) : (
            <>
              {/* Phones: one card per timesheet */}
              <ul className="md:hidden divide-y divide-slate-100">
                {timesheets.map(ts => (
                  <li key={ts.id} className={clsx('px-4 py-3', ts.is_admin_override && 'bg-amber-50/40')}>
                    <div className="flex items-center gap-2.5">
                      {isSelectable(ts) && (
                        <input type="checkbox" className="rounded w-4 h-4" checked={selected.includes(ts.id)} onChange={() => toggleSelect(ts.id)}
                          aria-label={`Select ${ts.staff_name}, ${shortDate(ts.shift_date)}`} />
                      )}
                      <PersonAvatar id={ts.staff_id} photo={ts.staff_photo} name={ts.staff_name} size="xs" />
                      <span className="font-medium text-sm text-slate-900 truncate flex-1">{ts.staff_name}</span>
                      <LateSignOutBadge ts={ts} onOpen={() => setPayLinesTs(ts)} />
                      <span className="text-xs text-slate-600 whitespace-nowrap">{shortDate(ts.shift_date)}</span>
                    </div>
                    <p className="mt-1 text-[13px] text-slate-600 truncate">
                      {ts.client_names || 'No client'} · {clockRange(ts)} · <span className="tabular-nums font-semibold text-slate-900">{ts.hours_worked ? `${parseFloat(ts.hours_worked).toFixed(2)} h` : '—'}</span>
                    </p>
                    {ts.is_admin_override && (
                      <p className="mt-1 flex items-center gap-1.5 text-[11px]">
                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Admin set</span>
                        {ts.admin_override_reason && <span className="text-slate-600 truncate">{ts.admin_override_reason}</span>}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      {ts.status !== 'approved' && (
                        <button type="button" onClick={() => approve(ts)} disabled={!!processing[ts.id] || !canApprove}
                          title={canApprove ? undefined : needsPermission('timesheets.approve')}
                          className="btn-primary flex-1 min-h-[40px]">
                          {processing[ts.id] === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          {ts.clock_in_time && !ts.clock_out_time ? 'No clock-out: set time' : ts.auto_clocked_out && !ts.auto_reviewed_at ? 'Auto clocked out: check' : 'Approve'}
                        </button>
                      )}
                      {ts.status === 'approved' && <StatusPill status="approved" />}
                      <OverflowMenu label={`More actions for ${ts.staff_name}`} className="!p-2.5" items={[
                        ts.progress_note && { label: 'View full note', icon: FileText, onClick: () => viewNotes(ts) },
                        hasPayLines(ts, ratesEngine) && { label: 'Pay lines', icon: Coins, onClick: () => setPayLinesTs(ts) },
                        ts.status === 'approved' && { label: processing[ts.id] === 'unapprove' ? 'Unapproving…' : 'Unapprove (reverse invoice)', icon: RotateCcw, disabled: !!processing[ts.id] || !canEditApproved, onClick: () => unapprove(ts) },
                      ]} />
                    </div>
                  </li>
                ))}
              </ul>

              {/* Wider screens: one line per timesheet, Approve always in view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-white">
                      <th scope="col" className="w-10 pl-4 pr-1 py-2.5 text-left">
                        <input type="checkbox" className="rounded"
                          aria-label="Select all timesheets in view"
                          checked={selected.length > 0 && selected.length === selectableIds().length}
                          onChange={toggleAll} />
                      </th>
                      <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Staff</th>
                      <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Date</th>
                      <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Shift type</th>
                      <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Client</th>
                      <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Clocked</th>
                      <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Hours</th>
                      <th scope="col" className={clsx(WIDE_ONLY, 'px-3 py-2.5 text-left text-xs font-semibold text-slate-500')}>Note</th>
                      <th scope="col" className={clsx(WIDE_ONLY, 'px-3 py-2.5 text-left text-xs font-semibold text-slate-500')}>Submitted</th>
                      <th scope="col" className="sticky right-0 z-[1] bg-white px-3 py-2.5 text-right text-xs font-semibold text-slate-500 shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.12)]">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheets.map(ts => (
                      <tr key={ts.id} className={clsx('group border-b border-slate-100 last:border-0', ts.is_admin_override ? 'bg-amber-50/40' : 'hover:bg-slate-50/70')}>
                        <td className="pl-4 pr-1 py-2">
                          {isSelectable(ts) && (
                            <input type="checkbox" className="rounded" checked={selected.includes(ts.id)} onChange={() => toggleSelect(ts.id)}
                              aria-label={`Select ${ts.staff_name}, ${shortDate(ts.shift_date)}`} />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <PersonAvatar id={ts.staff_id} photo={ts.staff_photo} name={ts.staff_name} size="xs" />
                            <span className="font-medium text-slate-900 whitespace-nowrap">{ts.staff_name}</span>
                            <LateSignOutBadge ts={ts} onOpen={() => setPayLinesTs(ts)} />
                            {ts.is_admin_override && (
                              <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold" title={ts.admin_override_reason || 'Times set by an admin'}>Admin set</span>
                            )}
                            {ts.is_admin_override && ts.admin_override_reason && (
                              <span className="text-[11px] text-slate-600 truncate max-w-[140px]" title={ts.admin_override_reason}>{ts.admin_override_reason}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{shortDate(ts.shift_date)}</td>
                        <td className="px-3 py-2"><ShiftTypeInline value={ts.shift_type} /></td>
                        <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate" title={ts.client_names || ''}>{ts.client_names || '—'}</td>
                        <td className="px-3 py-2 text-slate-700 text-[13px]">{clockRange(ts)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                            {ts.hours_worked ? `${parseFloat(ts.hours_worked).toFixed(2)} h` : <span className="text-slate-500 font-normal">—</span>}
                          </span>
                        </td>
                        <td className={clsx(WIDE_ONLY, 'px-3 py-2')}>
                          {ts.progress_note ? (
                            <button type="button" onClick={() => viewNotes(ts)} title="Read the full progress note"
                              className="group/note max-w-[220px] text-left flex items-start gap-1.5">
                              <FileText size={11} className="mt-0.5 shrink-0 text-slate-400 group-hover/note:text-primary-600" />
                              <span className="text-[12px] text-slate-600 leading-snug line-clamp-1 group-hover/note:text-primary-700">{ts.progress_note}</span>
                            </button>
                          ) : <span className="text-[12px] text-slate-500">—</span>}
                        </td>
                        <td className={clsx(WIDE_ONLY, 'px-3 py-2 text-xs text-slate-600 whitespace-nowrap')}>
                          {fmtDate(ts.created_at, 'd MMM, h:mm aaa')}
                        </td>
                        <td className={clsx('sticky right-0 z-[1] px-3 py-2 text-right shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.12)]',
                          ts.is_admin_override ? 'bg-amber-50' : 'bg-white group-hover:bg-slate-50')}>
                          <div className="flex items-center justify-end gap-1.5">
                            {ts.status !== 'approved' ? (
                              <button type="button" onClick={() => approve(ts)} disabled={!!processing[ts.id] || !canApprove}
                          title={canApprove ? undefined : needsPermission('timesheets.approve')}
                                aria-label={`Approve ${ts.staff_name}, ${shortDate(ts.shift_date)}`}
                                className="btn-secondary btn-sm whitespace-nowrap">
                                {processing[ts.id] === 'approve' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                {ts.clock_in_time && !ts.clock_out_time ? 'No clock-out: set time' : ts.auto_clocked_out && !ts.auto_reviewed_at ? 'Auto clocked out: check' : 'Approve'}
                              </button>
                            ) : <StatusPill status="approved" />}
                            <OverflowMenu label={`More actions for ${ts.staff_name}`} items={[
                              ts.progress_note && { label: 'View full note', icon: FileText, onClick: () => viewNotes(ts) },
                              hasPayLines(ts, ratesEngine) && { label: 'Pay lines', icon: Coins, onClick: () => setPayLinesTs(ts) },
                              ts.status === 'approved' && { label: processing[ts.id] === 'unapprove' ? 'Unapproving…' : 'Unapprove (reverse invoice)', icon: RotateCcw, disabled: !!processing[ts.id] || !canEditApproved, onClick: () => unapprove(ts) },
                            ]} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* CALENDAR VIEW — staff × days roster grid for the shown week.
          Rows = staff with timesheets that week, columns = Mon→Sun. Click a card to
          select it (pending cards open the same Review & approve flow). */}
      {!loadError && view === 'calendar' && (
        <div className="card p-4 space-y-3">
          {/* Week navigator */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              <button type="button" onClick={() => shiftCalWeek(-1)} aria-label="Previous week" className="p-2 rounded-lg hover:bg-white text-slate-600"><ChevronLeft size={16} /></button>
              <span className="text-sm font-semibold text-slate-800 px-3 min-w-[210px] text-center inline-flex items-center justify-center gap-1.5">
                <CalendarDays size={13} className="text-slate-500" />
                {fmtDate(calWeekStart, 'd MMM')} – {fmtDate(addDays(calWeekStart, 6), 'd MMM yyyy')}
              </span>
              <button type="button" onClick={() => shiftCalWeek(1)} aria-label="Next week" className="p-2 rounded-lg hover:bg-white text-slate-600"><ChevronRight size={16} /></button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Segmented ariaLabel="Group rows by" size="sm" value={calGroupBy} onChange={setCalGroupBy}
                options={[{ value: 'staff', label: 'By staff' }, { value: 'client', label: 'By client' }]} />
              <Segmented ariaLabel="Density" size="sm" value={calDensity} onChange={setCalDensity}
                options={[{ value: 'comfortable', label: 'Detailed' }, { value: 'compact', label: 'Compact' }]} />
              {/* Fold every staff row down to its summary — fits an all-clients week on one screen */}
              {calStaffRows.length > 0 && (
                <button type="button" onClick={toggleCollapseAll} className="btn-secondary btn-sm">
                  <ChevronDown size={13} className={clsx('transition-transform duration-150', everyCollapsed && '-rotate-90')} />
                  {everyCollapsed ? 'Expand all' : 'Collapse all'}
                </button>
              )}
              <button type="button" onClick={calThisWeek} className="btn-ghost btn-sm">This week</button>
            </div>
          </div>

          {/* Grand-totals audit bar — whole-week roll-up across every staff row shown.
              Lets you reconcile the all-clients sheet at a glance before drilling in. */}
          {calStaffRows.length > 0 && (
            <div className="flex flex-wrap items-stretch gap-2">
              {[
                { label: groupByClient ? 'Clients' : 'Staff', value: calStaffRows.length, cls: 'text-slate-900' },
                { label: 'Shifts',    value: grand.shifts,           cls: 'text-slate-900' },
                { label: 'Total hrs', value: fmtHrs(grand.hours),    cls: 'text-slate-900' },
                { label: 'Approved',  value: fmtHrs(grand.approved), cls: 'text-emerald-700' },
                { label: 'Pending',   value: fmtHrs(grand.pending),  cls: 'text-amber-700' },
                ...(grand.km > 0 ? [{ label: 'KM', value: fmtHrs(grand.km), cls: 'text-slate-900' }] : []),
              ].map(s => (
                <div key={s.label} className="flex-1 min-w-[92px] rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <p className="text-xs font-medium text-slate-500">{s.label}</p>
                  <p className={clsx('text-lg font-bold tabular-nums leading-tight', s.cls)}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <TableSkeleton rows={5} cols={7} />
          ) : calStaffRows.length === 0 ? (
            <EmptyState icon={CalendarDays} title="No timesheets this week" hint="Use the arrows to move between weeks, or switch the status filter to All." />
          ) : (
            <>
            <div className="overflow-x-auto -mx-1 px-1">
              <div className={gridMinW}>
                {/* Day header row */}
                <div className={clsx('grid gap-px bg-slate-200/70 rounded-t-xl overflow-hidden border border-slate-200 sticky top-0 z-30', gridCols)}>
                  <div className={clsx('bg-slate-50 text-xs font-semibold text-slate-600 flex items-center sticky left-0 z-40', compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5')}>{groupByClient ? 'Client' : 'Staff'}</div>
                  {calDayCols.map(c => {
                    const isToday = c.key === fmtDate(new Date(), 'yyyy-MM-dd');
                    return (
                      <div key={c.key} className={clsx('text-center', compact ? 'px-1.5 py-1' : 'px-2 py-2', isToday ? 'bg-primary-50' : 'bg-slate-50')}>
                        <p className={clsx('text-[11px] font-semibold', isToday ? 'text-primary-700' : 'text-slate-500')}>{fmtDate(c.date, 'EEE')}</p>
                        <p className={clsx('font-bold tabular-nums', compact ? 'text-xs' : 'text-sm', isToday ? 'text-primary-700' : 'text-slate-800')}>{fmtDate(c.date, compact ? 'd' : 'd MMM')}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Staff rows */}
                <div className="border-x border-b border-slate-200 rounded-b-xl overflow-hidden">
                  {calStaffRows.map((sr, ri) => {
                    const allTs        = Object.values(sr.days).flat();
                    const totalHours   = allTs.reduce((a, t) => a + tsHours(t), 0);
                    const approvedHrs  = allTs.filter(t => t.status === 'approved').reduce((a, t) => a + tsHours(t), 0);
                    const pendingHrs   = allTs.filter(t => t.status !== 'approved' && t.status !== 'rejected').reduce((a, t) => a + tsHours(t), 0);
                    const kmTotal      = allTs.reduce((a, t) => a + (parseFloat(t.mileage_km || t.mileage || 0) || 0), 0);
                    const shiftCount   = allTs.length;
                    const isCollapsed  = collapsedStaff.has(sr.name);
                    // Selectable ids for the row checkbox: approved rows drive bulk-unapprove
                    // when the Approved filter is active; otherwise pending rows drive bulk-approve.
                    const rowPend      = (statusFilter === 'approved'
                      ? allTs.filter(t => t.status === 'approved')
                      : allTs.filter(t => isPendingTs(t.status))).map(t => t.id);
                    const rowAllSel    = rowPend.length > 0 && rowPend.every(id => selected.includes(id));
                    return (
                    <div key={sr.name} className={clsx('grid gap-px bg-slate-100', gridCols, ri > 0 && 'mt-px')}>
                      {/* Row summary cell — sticky so it stays in view while scrolling the week
                          horizontally; the name button collapses/expands the row. */}
                      {compact ? (
                      <div className="bg-white px-2.5 py-2 flex items-center gap-2 border-r border-slate-100 sticky left-0 z-20">
                        {rowPend.length > 0 && (
                          <input type="checkbox" checked={rowAllSel}
                            onChange={() => toggleRowSelect(rowPend)}
                            aria-label={rowAllSel ? `Unselect ${sr.name}'s row` : `Select every ${statusFilter === 'approved' ? 'approved' : 'pending'} shift for ${sr.name}`}
                            className="shrink-0 w-3.5 h-3.5 rounded border-slate-300 cursor-pointer" />
                        )}
                        <button type="button" onClick={() => toggleStaffCollapse(sr.name)} aria-expanded={!isCollapsed}
                          className="flex items-center gap-2 min-w-0 flex-1 text-left rounded-md hover:bg-slate-50">
                          <ChevronDown size={13} className={clsx('shrink-0 text-slate-400 transition-transform duration-150', isCollapsed && '-rotate-90')} />
                          <PersonAvatar id={sr.staffId || sr.name} photo={sr.photo} name={sr.name} size="xs" square={groupByClient} />
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-[11px] text-slate-900 truncate leading-tight" title={sr.name}>{sr.name}</span>
                            <span className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[13px] font-bold text-slate-900 tabular-nums leading-none">{fmtHrs(totalHours)}<span className="text-[9px] text-slate-500 font-semibold ml-0.5">h</span></span>
                              <span className="inline-flex items-center gap-0.5 text-emerald-700 text-[10px] font-semibold tabular-nums" title="Approved hours"><Check size={8} strokeWidth={3} />{fmtHrs(approvedHrs)}</span>
                              <span className="inline-flex items-center gap-0.5 text-amber-700 text-[10px] font-semibold tabular-nums" title="Pending hours"><Clock size={8} />{fmtHrs(pendingHrs)}</span>
                            </span>
                          </span>
                        </button>
                      </div>
                      ) : (
                      <div className="bg-white px-3 py-3 flex flex-col gap-2 border-r border-slate-100 sticky left-0 z-20">
                        <div className="flex items-center gap-2">
                          {rowPend.length > 0 && (
                            <input type="checkbox" checked={rowAllSel}
                              onChange={() => toggleRowSelect(rowPend)}
                              aria-label={rowAllSel ? `Unselect ${sr.name}'s row` : `Select every ${statusFilter === 'approved' ? 'approved' : 'pending'} shift for ${sr.name}`}
                              className="shrink-0 w-4 h-4 rounded border-slate-300 cursor-pointer" />
                          )}
                          <button type="button" onClick={() => toggleStaffCollapse(sr.name)} aria-expanded={!isCollapsed}
                            className="flex items-center gap-2 min-w-0 flex-1 text-left rounded-md hover:bg-slate-50">
                            <ChevronDown size={15} className={clsx('shrink-0 text-slate-400 transition-transform duration-150', isCollapsed && '-rotate-90')} />
                            <PersonAvatar id={sr.staffId || sr.name} photo={sr.photo} name={sr.name} size="sm" square={groupByClient} />
                            <span className="block font-semibold text-[13px] text-slate-900 truncate leading-tight" title={sr.name}>{sr.name}</span>
                          </button>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-2xl font-bold text-slate-900 tabular-nums tracking-tight leading-none">{fmtHrs(totalHours)}</span>
                          <span className="text-[11px] font-semibold text-slate-500">hrs</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-800 text-[11px] font-semibold tabular-nums" title="Approved hours">
                            <Check size={9} strokeWidth={3} />{fmtHrs(approvedHrs)}
                          </span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 text-[11px] font-semibold tabular-nums" title="Pending hours">
                            <Clock size={9} />{fmtHrs(pendingHrs)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[11px] text-slate-500 font-medium pt-0.5 border-t border-slate-100">
                          <span className="inline-flex items-center gap-1 pt-1"><CalendarDays size={10} />{shiftCount} shift{shiftCount !== 1 ? 's' : ''}</span>
                          {kmTotal > 0 && <span className="inline-flex items-center gap-1 pt-1"><Car size={10} />{fmtHrs(kmTotal)} km</span>}
                        </div>
                      </div>
                      )}
                      {/* Day cells (collapsed → one summary strip spanning the week) */}
                      {isCollapsed ? (
                        <div className="col-span-7 bg-white px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                          <span className="inline-flex items-center gap-1"><CalendarDays size={11} />{shiftCount} shift{shiftCount !== 1 ? 's' : ''}</span>
                          <span className="tabular-nums text-slate-800 font-semibold">{fmtHrs(totalHours)} h total</span>
                          <span className="inline-flex items-center gap-1 text-emerald-700 tabular-nums"><Check size={10} strokeWidth={3} />{fmtHrs(approvedHrs)} approved</span>
                          <span className="inline-flex items-center gap-1 text-amber-700 tabular-nums"><Clock size={10} />{fmtHrs(pendingHrs)} pending</span>
                          {kmTotal > 0 && <span className="inline-flex items-center gap-1 tabular-nums"><Car size={10} />{fmtHrs(kmTotal)} km</span>}
                          <span className="ml-auto text-slate-500 italic">Collapsed. Select the name to expand.</span>
                        </div>
                      ) : calDayCols.map(c => {
                        const items   = (sr.days[c.key] || []).slice().sort((a, b) => startMins(a) - startMins(b));
                        const isToday = c.key === fmtDate(new Date(), 'yyyy-MM-dd');
                        return (
                          <div key={c.key} className={clsx(isToday ? 'bg-primary-50/30' : 'bg-white', compact ? 'px-1 py-1 space-y-1 min-h-[40px]' : 'px-1.5 py-1.5 space-y-1.5 min-h-[72px]')}>
                            {items.map(t => {
                              const svc        = parseService(t.shift_type);
                              const meta       = statusMeta(t.status);
                              const km         = parseFloat(t.mileage_km || t.mileage || 0) || 0;
                              const hasNote    = !!t.progress_note;
                              const noClockOut = !t.clock_out_time;
                              const start      = t.sched_start_hm || t.scheduled_start;
                              const end        = t.sched_end_hm || t.scheduled_end;
                              const timeStr    = `${start ? fmtTime12(start) : fmtDate(t.clock_in_time, 'h:mm a', '—')}${end ? `–${fmtTime12(end)}` : ''}`;
                              const openCard   = () => {
                                if (isPendingTs(t.status)) { setSelected([t.id]); openReview([t.id]); }
                                else toggleSelect(t.id);
                              };
                              return (
                              <div key={t.id} role="button" tabIndex={0}
                                onClick={openCard}
                                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openCard(); } }}
                                aria-label={`${t.staff_name || 'Shift'} · ${timeStr} · ${meta.label}${isPendingTs(t.status) ? ' · open review' : ''}`}
                                title={`${t.shift_type || 'Shift'}\n${t.client_names || 'No client'} · ${meta.label} · ${fmtHrs(tsHours(t))}h${km > 0 ? ` · ${fmtHrs(km)}km` : ''}${hasNote ? ' · note' : ''}${noClockOut ? ' · not clocked out' : ''}`}
                                className={clsx(
                                  'relative w-full text-left rounded-lg border border-slate-200/80 border-l-[3px] bg-white hover:border-slate-300 transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
                                  compact ? 'px-1.5 py-1' : 'px-2 py-1.5',
                                  statusBorder(t.status),
                                  selected.includes(t.id) && 'ring-1 ring-primary-400 border-primary-300'
                                )}>
                                {compact ? (
                                <>
                                  {/* Dense single line: dot · time · icons · hours */}
                                  <div className="flex items-center gap-1">
                                    {isSelectable(t) ? (
                                      <input type="checkbox" checked={selected.includes(t.id)}
                                        onClick={e => e.stopPropagation()}
                                        onChange={() => toggleSelect(t.id)}
                                        aria-label={statusFilter === 'approved' ? 'Select to unapprove' : 'Select for approval'}
                                        className="shrink-0 w-3 h-3 rounded border-slate-300 cursor-pointer" />
                                    ) : (
                                      <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', statusDot(t.status))} title={meta.label} />
                                    )}
                                    <span className="text-[10px] font-semibold text-slate-800 tabular-nums truncate">{timeStr}</span>
                                    <span className="ml-auto flex items-center gap-0.5 shrink-0">
                                      {hasNote    && <FileText size={9} className="text-slate-500" />}
                                      {km > 0     && <Car size={9} className="text-slate-500" />}
                                      {noClockOut && <AlertTriangle size={9} className="text-amber-600" />}
                                      {t.is_admin_override && <Lock size={9} className="text-slate-500" />}
                                      <span className="text-[10px] font-bold text-slate-600 tabular-nums">{fmtHrs(tsHours(t))}h</span>
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-slate-500 truncate leading-tight">{svc.service} · {groupByClient ? (t.staff_name || 'Unassigned') : (t.client_names || 'No client')}</p>
                                </>
                                ) : (
                                <>
                                {/* Time range + duration */}
                                <div className="flex items-center justify-between gap-1">
                                  <span className="flex items-center gap-1 min-w-0">
                                    {isSelectable(t) && (
                                      <input type="checkbox" checked={selected.includes(t.id)}
                                        onClick={e => e.stopPropagation()}
                                        onChange={() => toggleSelect(t.id)}
                                        aria-label={statusFilter === 'approved' ? 'Select to unapprove' : 'Select for approval'}
                                        className="shrink-0 w-3.5 h-3.5 rounded border-slate-300 cursor-pointer" />
                                    )}
                                    <span className="text-[11px] font-semibold text-slate-800 tabular-nums truncate">{timeStr}</span>
                                  </span>
                                  <span className="shrink-0 text-[10px] font-bold text-slate-600 bg-slate-100 rounded px-1 py-0.5 tabular-nums">{fmtHrs(tsHours(t))}h</span>
                                </div>
                                {/* Service type */}
                                <p className="text-[11px] font-medium text-slate-800 truncate mt-1 leading-tight" title={t.shift_type}>{svc.service}</p>
                                {svc.band && (
                                  <span className="inline-block mt-0.5 px-1 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 leading-none">{svc.band}</span>
                                )}
                                {/* Client / program (or worker when grouped by client) */}
                                <p className="text-[10px] text-slate-500 truncate mt-1">{groupByClient ? (t.staff_name || 'Unassigned') : (t.client_names || 'No client')}</p>
                                {/* Footer: status + attribute icons */}
                                <div className="flex items-center gap-1 mt-1.5 pt-1 border-t border-slate-100">
                                  <span className={clsx('inline-flex items-center gap-1 text-[10px] font-semibold', meta.cls)}>
                                    <span className={clsx('w-1.5 h-1.5 rounded-full', statusDot(t.status))} />{meta.label}
                                  </span>
                                  <span className="ml-auto flex items-center gap-1">
                                    {hasNote    && <FileText size={10} className="text-slate-500" />}
                                    {km > 0     && <Car size={10} className="text-slate-500" />}
                                    {noClockOut && <AlertTriangle size={10} className="text-amber-600" />}
                                    {t.is_admin_override && <Lock size={10} className="text-slate-500" />}
                                  </span>
                                </div>
                                </>
                                )}
                              </div>
                            );})}
                          </div>
                        );
                      })}
                    </div>
                  );})}
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 pt-1 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Approved</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" />Pending</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" />Rejected</span>
              <span className="inline-flex items-center gap-1.5"><FileText size={11} className="text-slate-500" />Progress note</span>
              <span className="inline-flex items-center gap-1.5"><Car size={11} className="text-slate-500" />Mileage</span>
              <span className="inline-flex items-center gap-1.5"><AlertTriangle size={11} className="text-amber-600" />Not clocked out</span>
              <span className="inline-flex items-center gap-1.5"><Lock size={11} className="text-slate-500" />Admin set</span>
            </div>
            </>
          )}
        </div>
      )}

      {/* Selection bar (shared by List + Calendar) — tick rows/cards then act together.
          Approved filter → bulk-unapprove; otherwise → Review & approve. */}
      <BulkBar count={selected.length} onClear={() => setSelected([])}
        noun={statusFilter === 'approved' ? `approved timesheet${selected.length === 1 ? '' : 's'} selected` : `timesheet${selected.length === 1 ? '' : 's'} selected`}>
        {statusFilter === 'approved' ? (
          <button type="button" onClick={bulkUnapprove} disabled={bulkUnapproving || !canEditApproved} className="btn-secondary btn-sm"
            title={canEditApproved ? undefined : needsPermission('timesheets.edit_approved')}>
            <RotateCcw size={13} /> {bulkUnapproving ? 'Unapproving…' : 'Unapprove selected'}
          </button>
        ) : (
          <button type="button" onClick={() => openReview(selected)} disabled={!canApprove} className="btn-primary btn-sm"
            title={canApprove ? undefined : needsPermission('timesheets.approve')}>
            <ClipboardCheck size={13} /> Review &amp; approve selected
          </button>
        )}
      </BulkBar>

      {travelModal && (
        <AutoTravelModal staff={staff} onClose={() => setTravelModal(false)}
          onApplied={() => load()} />
      )}
      {/* Bulk review + approve modal */}
      {bulkIds && (
        <BulkApproveModal ids={bulkIds} onClose={closeReview} onDone={onReviewDone} />
      )}

      {/* Full progress-note detail (from the list "View full note") */}
      {noteModal && (
        <NoteDetailModal row={noteModal} onClose={() => setNoteModal(null)} />
      )}

      {/* Pay lines (Pay Rates): lines, late sign-out decision, public holiday warning */}
      <PayLinesDialog ts={payLinesTs} open={!!payLinesTs} onClose={() => setPayLinesTs(null)} onChanged={() => load()} />
    </div>
  );
}

// Last day of the month containing `dateStr` (YYYY-MM-DD), as YYYY-MM-DD.
function endOfMonthStr(dateStr) {
  const d = new Date(dateStr);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return isoDate(end);
}

const PERIODS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

export default function Timesheets() {
  const confirm = useConfirm();
  const [tab, setTab]               = useUrlTab('tab', 'pending', ['pending', 'weekly', 'xero', 'contractor-bills', 'employment-hero', 'quickbooks']);
  const connected = useConnectedIntegrations();
  const [pendingCount, setPendingCount] = useState(0);
  const [billingNotes, setBillingNotes] = useState(null);   // { context, reasons[] } — stays until dismissed
  const [weekStart, setWeekStart] = useState(() =>
    isoDate(getMonday(new Date()))
  );
  const [period, setPeriod]         = useState('weekly');   // weekly | fortnightly | monthly | custom
  const [customEnd, setCustomEnd]   = useState('');         // YYYY-MM-DD for custom range
  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [reportError, setReportError] = useState(null);
  const [approving, setApproving]   = useState({});
  const [anomalies, setAnomalies]   = useState({});
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [staffTimesheets, setStaffTimesheets] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [processingTs, setProcessingTs] = useState({});
  const [payLinesTs, setPayLinesTs] = useState(null);
  const ratesEngine = usePayEngine() === 'rates';
  const { can: canPay } = usePayPermissions();
  const canApproveStaff = canPay('timesheets.approve');
  const canUnapproveStaff = canPay('timesheets.edit_approved');

  // Resolve the report end date from the selected period.
  const periodEnd = useCallback(() => {
    if (period === 'fortnightly') return isoDate(addDays(new Date(weekStart), 13));
    if (period === 'monthly')     return endOfMonthStr(weekStart);
    if (period === 'custom')      return customEnd || isoDate(addDays(new Date(weekStart), 6));
    return isoDate(addDays(new Date(weekStart), 6));   // weekly
  }, [period, weekStart, customEnd]);

  const load = useCallback(() => {
    setLoading(true);
    setReportError(null);
    const endDate = periodEnd();
    api.get(`/timesheets/report?weekStart=${weekStart}&endDate=${endDate}`)
      .then(r => setReport(r.data))
      .catch((e) => setReportError(e.response?.data?.message || "We couldn't load the staff hours report. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [weekStart, periodEnd]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/timesheets/anomalies').then(r => setAnomalies(r.data)).catch(() => {});
  }, []);

  // Step navigation uses the period span (week / fortnight / month / custom span).
  const shiftWeek = (n) => {
    const d = new Date(weekStart);
    let span = 7;
    if (period === 'fortnightly') span = 14;
    else if (period === 'monthly') { d.setMonth(d.getMonth() + n); setWeekStart(isoDate(d)); return; }
    else if (period === 'custom' && customEnd) {
      span = Math.max(1, Math.round((new Date(customEnd) - new Date(weekStart)) / 86400000) + 1);
    }
    d.setDate(d.getDate() + n * span);
    setWeekStart(isoDate(d));
  };

  const periodText = `${fmtISO(report?.weekStart || weekStart, 'd MMM')} – ${fmtISO(report?.weekEnd || periodEnd(), 'd MMM yyyy')}`;

  // Approve every pending timesheet for one person in the period — it bills them, so it asks.
  const approveAll = async (row) => {
    if (!report) return;
    const staffId = row.id;
    const ok = await confirm(
      `${plural(row.pending, 'completed timesheet')} in ${periodText} ${row.pending === 1 ? 'is' : 'are'} waiting. Approving creates their invoice lines and draws down client funds. Timesheets marked Review are left for Review & approve.`,
      { title: `Approve ${row.name}'s pending timesheets?`, confirmLabel: 'Approve all' });
    if (!ok) return;
    setApproving(a => ({ ...a, [staffId]: true }));
    try {
      const { data } = await api.post('/timesheets/staff-approve', {
        staffId,
        weekStart: report.weekStart,
        weekEnd: report.weekEnd,
      });
      const note = billingNote(data?.billing, row.name);
      if (note) {
        setBillingNotes(note);
        toast(`${data?.message || 'Approved'}. Billing needs a look — see the note on the page.`, { icon: warnIcon, duration: 6000 });
      } else {
        toast.success(data?.message || 'All pending timesheets approved');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to approve timesheets');
    } finally {
      setApproving(a => ({ ...a, [staffId]: false }));
    }
  };

  const toggleStaff = async (staffId) => {
    if (expandedStaff === staffId) { setExpandedStaff(null); return; }
    setExpandedStaff(staffId);
    setLoadingDetail(true);
    try {
      const end = periodEnd();
      const r = await api.get(`/timesheets?staff_id=${staffId}&start_date=${weekStart}&end_date=${end}`);
      setStaffTimesheets(r.data || []);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to load timesheets'); }
    finally { setLoadingDetail(false); }
  };

  // Unapprove a staff timesheet from the expanded detail: reverses its invoice and
  // refunds the fund, then moves it back to Pending (keeps the record).
  const unapproveStaffTs = async (ts) => {
    const ok = await confirm('This reverses its invoice and refunds the fund, then moves it back to Pending.', {
      title: `Unapprove the timesheet for ${shortDate(ts.shift_date)}?`, confirmLabel: 'Unapprove', danger: true,
    });
    if (!ok) return;
    const tsId = ts.id;
    setProcessingTs(d => ({ ...d, [tsId]: true }));
    try {
      await api.post(`/timesheets/${tsId}/unapprove`);
      toast.success('Timesheet moved back to pending');
      setStaffTimesheets(prev => prev.map(t => t.id === tsId ? { ...t, status: 'pending' } : t));
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to unapprove'); }
    finally { setProcessingTs(d => { const n = {...d}; delete n[tsId]; return n; }); }
  };

  const staffRows   = report?.staff || [];

  const bandTotals = BANDS.reduce((acc, b) => {
    acc[b.key] = staffRows.reduce((s, r) => s + (parseFloat(r[b.key]) || 0), 0);
    return acc;
  }, {});
  const grandTotal = staffRows.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);
  const payTotal = staffRows.reduce((s, r) => s + (parseFloat(r.pay_total) || 0), 0);

  const downloadCSV = () => {
    if (!staffRows.length) return;
    const header = ['Name', 'Pay Group', 'Pending', 'Approved', 'Rejected',
      ...BANDS.map(b => b.label), 'Total', 'Mileage (km)', 'Expense ($)', 'Sleepover'];
    const rows = staffRows.map(r => [
      r.name, r.payGroup || '', r.pending, r.approved, r.rejected,
      ...BANDS.map(b => (parseFloat(r[b.key]) || 0).toFixed(2)),
      (parseFloat(r.total) || 0).toFixed(2),
      (parseFloat(r.mileage) || 0).toFixed(1),
      (parseFloat(r.expense) || 0).toFixed(2),
      r.sleepover || 0,
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheets-week-ending-${report?.weekEnd || weekStart}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Xero Payroll CSV — format compatible with Xero's bulk timesheet import
  const downloadXeroCSV = () => {
    const approvedStaff = staffRows.filter(r => parseFloat(r.approved) > 0 || parseFloat(r.total) > 0);
    if (!approvedStaff.length) return toast.error('No approved timesheets in this week');
    // Xero columns: EmployeeName, PayItem, UnitType, Quantity, Rate, StartDate, EndDate
    const header = ['EmployeeName', 'PayItem', 'UnitType', 'Quantity', 'Rate', 'StartDate', 'EndDate'];
    const rows = [];
    for (const r of approvedStaff) {
      for (const b of BANDS) {
        const hrs = parseFloat(r[b.key]) || 0;
        if (hrs <= 0) continue;
        rows.push([
          `"${r.name}"`, `"Ordinary Hours — ${b.label}"`, 'Hours',
          hrs.toFixed(2), '',
          weekStart, report?.weekEnd || weekStart,
        ]);
      }
      const mileage = parseFloat(r.mileage) || 0;
      if (mileage > 0) {
        rows.push([`"${r.name}"`, '"Kilometres Allowance"', 'KM',
          mileage.toFixed(1), '', weekStart, report?.weekEnd || weekStart]);
      }
    }
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `xero-timesheet-${weekStart}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} payroll lines for Xero`);
  };

  const cellNum = (v, dp = 1) => {
    const n = parseFloat(v) || 0;
    return n > 0 ? <span className="tabular-nums text-slate-800">{n.toFixed(dp)}</span> : <span className="text-slate-400">—</span>;
  };

  return (
    <div className="space-y-5">
      {/* The top bar already titles the page; the tabs name its screens. */}
      <Tabs ariaLabel="Timesheets" value={tab} onChange={setTab}
        tabs={[
          { id: 'pending', label: 'Manage timesheets', count: pendingCount > 0 ? pendingCount : undefined },
          { id: 'weekly', label: 'Staff hours report' },
          // A payroll tab shows only when that software is connected (Settings › Integrations).
          ...(connected.xero || tab === 'xero' ? [{ id: 'xero', label: 'Xero payroll' }] : []),
          // Contractors on Pay Rates are paid by Xero bill plus an ABA file (spec section 8).
          ...((ratesEngine && canPay('payroll.send_xero')) || tab === 'contractor-bills' ? [{ id: 'contractor-bills', label: 'Contractor bills' }] : []),
          ...(connected.employmentHero || tab === 'employment-hero' ? [{ id: 'employment-hero', label: 'Employment Hero payroll' }] : []),
          ...(connected.quickbooks || tab === 'quickbooks' ? [{ id: 'quickbooks', label: 'QuickBooks' }] : []),
        ]} />

      {/* Approved, but billing did not complete — money at risk, so it stays until dismissed. */}
      {billingNotes && (
        <Notice tone="amber" role="alert" onDismiss={() => setBillingNotes(null)}
          title={`Approved, but some billing did not complete${billingNotes.context ? ` (${billingNotes.context})` : ''}`}
          actions={(
            <>
              <Link to="/invoices?tab=invoices" className="btn-secondary btn-sm">Open invoices</Link>
              <Link to="/financials/reports/audit" className="btn-secondary btn-sm">Open the rates audit</Link>
            </>
          )}>
          <ul className="list-disc pl-4 space-y-0.5">
            {billingNotes.reasons.map(r => <li key={r.reason}>{r.reason}{r.n > 1 ? ` (${r.n} times)` : ''}</li>)}
          </ul>
          <p className="mt-1">The timesheets stay approved, but the invoice lines above were not created. Fix the cause, such as the client's funding or a missing rate, then check the client's invoices for these shifts.</p>
        </Notice>
      )}

      {tab === 'xero' && <XeroPayrollTab />}
      {tab === 'contractor-bills' && <ContractorBillsTab />}
      {tab === 'employment-hero' && <EmploymentHeroPayrollTab />}
      {tab === 'quickbooks' && <QuickBooksPayrollTab />}

      {/* Pending approval tab */}
      {tab === 'pending' && (
        <PendingApprovalTab onCountChange={setPendingCount} onBillingNote={setBillingNotes} />
      )}

      {/* Staff hours report tab */}
      {tab === 'weekly' && <>

      {/* Period navigator + exports */}
      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          <button type="button" onClick={() => shiftWeek(-1)} aria-label="Previous period"
            className="p-2 rounded-lg hover:bg-white text-slate-600">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-slate-800 px-3 min-w-[200px] text-center tabular-nums">
            {periodText}
          </span>
          <button type="button" onClick={() => shiftWeek(1)} aria-label="Next period"
            className="p-2 rounded-lg hover:bg-white text-slate-600">
            <ChevronRight size={16} />
          </button>
        </div>

        <Segmented ariaLabel="Report period" options={PERIODS} value={period} onChange={setPeriod} size="sm" />

        {/* Custom range pickers */}
        {period === 'custom' && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <label htmlFor="ts-range-from" className="sr-only">From</label>
            <input id="ts-range-from" type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)}
              className="input-field !w-auto !py-1.5 text-[13px]" />
            <span aria-hidden="true">–</span>
            <label htmlFor="ts-range-to" className="sr-only">To</label>
            <input id="ts-range-to" type="date" value={customEnd} min={weekStart} onChange={e => setCustomEnd(e.target.value)}
              className="input-field !w-auto !py-1.5 text-[13px]" />
          </div>
        )}

        <button type="button" onClick={() => setWeekStart(isoDate(getMonday(new Date())))} className="btn-ghost btn-sm">
          This week
        </button>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button type="button" onClick={downloadCSV} disabled={!staffRows.length} className="btn-secondary btn-sm"><Download size={13} /> Download CSV</button>
          <button type="button" onClick={() => window.print()} className="btn-secondary btn-sm"><Printer size={13} /> Print</button>
          <button type="button" onClick={downloadXeroCSV} className="btn-secondary btn-sm"
            title="Export approved timesheets as a Xero Payroll bulk-import CSV">
            <FileSpreadsheet size={13} /> Export for Xero
          </button>
        </div>
      </div>

      {/* Summary */}
      {!loading && !reportError && staffRows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile label="Total hours" accent="primary" value={`${grandTotal.toFixed(1)} h`} sub="across all staff" />
          <KpiTile label="Staff" accent="slate" value={staffRows.length} sub="on payroll this period" />
          <KpiTile label="Pending" accent="amber" value={staffRows.reduce((s, r) => s + r.pending, 0)} sub="timesheets to approve" />
          <KpiTile label="Approved" accent="emerald" value={staffRows.reduce((s, r) => s + r.approved, 0)} sub="ready to export" />
        </div>
      )}

      {/* Report table */}
      <div className="card overflow-hidden">
        {reportError ? (
          <ErrorState message={reportError} onRetry={load} />
        ) : loading ? (
          <TableSkeleton rows={6} cols={8} />
        ) : staffRows.length === 0 ? (
          <EmptyState icon={Clock} title="No timesheets in this period" hint="Pick another period, or check that shifts in it have been worked and clocked out." />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            {/* Column groups header */}
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th scope="col" className="text-left px-4 py-2 text-xs font-semibold text-slate-500 sticky left-0 bg-white min-w-[200px]" rowSpan={2}>
                  Staff and pay group
                </th>
                <th scope="col" className="text-center px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  Approval
                </th>
                <th scope="colgroup" colSpan={4} className="text-center px-3 py-2 text-xs font-semibold text-slate-500 border-l border-slate-200">
                  Weekday hours
                </th>
                <th scope="col" className="text-center px-3 py-2 text-xs font-semibold text-slate-500 border-l border-slate-200" rowSpan={2}>
                  Sat
                </th>
                <th scope="col" className="text-center px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  Sun
                </th>
                <th scope="col" className="text-center px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  PH
                </th>
                <th scope="col" className="text-right px-3 py-2 text-xs font-semibold text-slate-500 border-l border-slate-200" rowSpan={2}>
                  Total
                </th>
                <th scope="col" className="text-right px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  Mileage
                </th>
                <th scope="col" className="text-right px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  Expense
                </th>
                <th scope="col" className="text-right px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  Sleepover
                </th>
                <th scope="col" className="text-right px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  Est. pay
                </th>
                <th scope="col" className="text-center px-3 py-2 text-xs font-semibold text-slate-500" rowSpan={2}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
              <tr className="bg-white border-b border-slate-200">
                {BANDS.filter(b => b.key.startsWith('wd_')).map(b => (
                  <th scope="col" key={b.key} className={clsx('text-center px-2 py-1.5 text-[11px] font-semibold text-slate-500 whitespace-nowrap', b.key === 'wd_early' && 'border-l border-slate-200')}>
                    {b.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staffRows.map(row => {
                const hasPending = row.pending > 0;
                const staffAnomalies = anomalies[row.id];
                const anomalyCount = staffAnomalies?.issues?.length || 0;
                const anomalyTip = staffAnomalies?.issues?.flatMap(i => i.flags).join(' · ') || '';
                const isExpanded = expandedStaff === row.id;

                return (
                  <React.Fragment key={row.id}>
                  <tr className={clsx('border-b border-slate-100 transition-colors duration-150 cursor-pointer',
                    isExpanded ? 'bg-primary-50/40' : anomalyCount > 0 ? 'bg-amber-50/40 hover:bg-amber-50/70' : 'hover:bg-slate-50/70')}
                    onClick={() => toggleStaff(row.id)}>
                    {/* Staff name */}
                    <td className={clsx('px-4 py-3 sticky left-0', isExpanded ? 'bg-primary-50' : 'bg-white')}>
                      <div className="flex items-center gap-2.5">
                        <ChevronRight size={14} className={clsx('text-slate-400 shrink-0 transition-transform duration-150', isExpanded && 'rotate-90')} />
                        <PersonAvatar id={row.id} name={row.name} size="sm" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-slate-900 text-sm truncate">{row.name}</p>
                            {anomalyCount > 0 && (
                              <span title={anomalyTip} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full shrink-0 cursor-help">
                                <AlertTriangle size={9} />{anomalyCount}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{row.payGroup || 'Standard'}</p>
                        </div>
                      </div>
                    </td>

                    {/* Approval status */}
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-center gap-0.5">
                        {row.approved > 0 && <span className="badge-green !text-[10px] whitespace-nowrap">{row.approved} approved</span>}
                        {row.pending > 0 && <span className="badge-yellow !text-[10px] whitespace-nowrap">{row.pending} pending</span>}
                        {row.rejected > 0 && <span className="badge-red !text-[10px] whitespace-nowrap">{row.rejected} rejected</span>}
                        {row.pending === 0 && row.approved === 0 && row.rejected === 0 && (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </td>

                    {/* Weekday bands */}
                    {BANDS.filter(b => b.key.startsWith('wd_')).map(b => (
                      <td key={b.key} className={clsx('px-2 py-3 text-center text-xs', b.key === 'wd_early' && 'border-l border-slate-200')}>
                        {cellNum(row[b.key])}
                      </td>
                    ))}

                    {/* Saturday / Sunday / PH */}
                    <td className="px-2 py-3 text-center text-xs border-l border-slate-200">{cellNum(row.saturday)}</td>
                    <td className="px-2 py-3 text-center text-xs">{cellNum(row.sunday)}</td>
                    <td className="px-2 py-3 text-center text-xs">{cellNum(row.public_holiday)}</td>

                    {/* Total */}
                    <td className="px-3 py-3 text-right border-l border-slate-200 whitespace-nowrap">
                      <span className="font-bold text-slate-900 text-sm tabular-nums">{(parseFloat(row.total) || 0).toFixed(1)} h</span>
                    </td>

                    {/* Mileage */}
                    <td className="px-3 py-3 text-right text-xs text-slate-700 tabular-nums whitespace-nowrap">
                      {(parseFloat(row.mileage) || 0) > 0 ? `${parseFloat(row.mileage).toFixed(0)} km` : <span className="text-slate-400">—</span>}
                    </td>

                    {/* Expense */}
                    <td className="px-3 py-3 text-right text-xs text-slate-800 tabular-nums whitespace-nowrap">
                      {(parseFloat(row.expense) || 0) > 0 ? money(row.expense) : <span className="text-slate-400">—</span>}
                    </td>

                    {/* Sleepover */}
                    <td className="px-3 py-3 text-right text-xs text-slate-700 tabular-nums">
                      {(row.sleepover || 0) > 0 ? row.sleepover : <span className="text-slate-400">—</span>}
                    </td>

                    {/* Est. pay */}
                    <td className="px-3 py-3 text-right text-xs font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                      {(parseFloat(row.pay_total) || 0) > 0 ? money(row.pay_total) : <span className="text-slate-400 font-normal">—</span>}
                    </td>

                    {/* Action */}
                    <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex flex-col items-stretch gap-1">
                        {hasPending && (
                          <button type="button" onClick={() => approveAll(row)} disabled={!!approving[row.id] || !canApproveStaff}
                            aria-label={`Approve all pending timesheets for ${row.name}`}
                            className="btn-secondary btn-sm whitespace-nowrap">
                            {approving[row.id] ? <Loader2 size={11} className="animate-spin" /> : <CheckCheck size={11} />}
                            Approve all
                          </button>
                        )}
                        <button type="button" onClick={() => toggleStaff(row.id)} aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Close' : 'View'} timesheets for ${row.name}`}
                          className="btn-ghost btn-sm whitespace-nowrap">
                          {isExpanded ? 'Close' : 'View'}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded timesheets detail */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={15} className="bg-slate-50/60 border-b border-slate-100 px-6 py-3">
                        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
                          <RotateCcw size={12} className="shrink-0" />
                          <span>Unapproving an approved timesheet <strong>reverses its client invoice and refunds the fund</strong>, then moves it back to Pending.</span>
                        </div>
                        {loadingDetail ? (
                          <TableSkeleton rows={3} cols={5} />
                        ) : staffTimesheets.length === 0 ? (
                          <p className="text-xs text-slate-500 py-1">No timesheets found for this period.</p>
                        ) : (
                          <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[11px] font-semibold text-slate-500">
                                <th scope="col" className="text-left pb-1.5 pr-4">Date</th>
                                <th scope="col" className="text-left pb-1.5 pr-4">Shift</th>
                                <th scope="col" className="text-left pb-1.5 pr-4">Client</th>
                                <th scope="col" className="text-left pb-1.5 pr-4">Clocked</th>
                                <th scope="col" className="text-right pb-1.5 pr-4">Hours</th>
                                <th scope="col" className="text-left pb-1.5 pr-4">Status</th>
                                <th scope="col" className="text-right pb-1.5"><span className="sr-only">Actions</span></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100/80">
                              {staffTimesheets.map(ts => (
                                <tr key={ts.id} className="hover:bg-white/60">
                                  <td className="py-1.5 pr-4 text-slate-700 whitespace-nowrap">{shortDate(ts.shift_date)}</td>
                                  <td className="py-1.5 pr-4 text-slate-600 whitespace-nowrap tabular-nums">
                                    {fmtTime12(ts.scheduled_start)} – {fmtTime12(ts.scheduled_end)}
                                  </td>
                                  <td className="py-1.5 pr-4 text-slate-600 max-w-[140px] truncate">{ts.client_names || '—'}</td>
                                  <td className="py-1.5 pr-4 text-slate-700">{fmtDate(ts.clock_in_time, 'h:mm aaa')} – {fmtDate(ts.clock_out_time, 'h:mm aaa')}</td>
                                  <td className="py-1.5 pr-4 text-right font-semibold text-slate-800 tabular-nums">
                                    {ts.hours_worked ? `${parseFloat(ts.hours_worked).toFixed(2)} h` : '—'}
                                  </td>
                                  <td className="py-1.5 pr-4">
                                    <span className="inline-flex items-center gap-1.5">
                                      <StatusPill status={ts.status || 'pending'} />
                                      <LateSignOutBadge ts={ts} onOpen={() => setPayLinesTs({ ...ts, staff_name: row.name })} />
                                    </span>
                                  </td>
                                  <td className="py-1.5 text-right">
                                    <span className="inline-flex items-center justify-end gap-1.5">
                                    {hasPayLines(ts, ratesEngine) && (
                                      <button type="button" onClick={() => setPayLinesTs({ ...ts, staff_name: row.name })}
                                        className="btn-ghost btn-sm whitespace-nowrap">
                                        <Coins size={10} aria-hidden="true" />Pay lines
                                      </button>
                                    )}
                                    {ts.status === 'approved' ? (
                                      <button type="button" onClick={() => unapproveStaffTs(ts)} disabled={!!processingTs[ts.id] || !canUnapproveStaff}
                                        title={canUnapproveStaff ? 'Move back to pending: reverses the invoice and refunds the fund' : needsPermission('timesheets.edit_approved')}
                                        className="btn-secondary btn-sm whitespace-nowrap">
                                        {processingTs[ts.id] ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                                        Unapprove
                                      </button>
                                    ) : (
                                      <span className="text-[11px] text-slate-400">—</span>
                                    )}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200">
                <td className="px-4 py-3 text-sm font-bold text-slate-800 sticky left-0 bg-slate-50">
                  Totals ({staffRows.length} staff)
                </td>
                <td className="px-3 py-3" />
                {BANDS.filter(b => b.key.startsWith('wd_')).map(b => (
                  <td key={b.key} className={clsx('px-2 py-3 text-center text-xs font-bold text-slate-900 tabular-nums', b.key === 'wd_early' && 'border-l border-slate-200')}>
                    {(parseFloat(bandTotals[b.key]) || 0) > 0 ? parseFloat(bandTotals[b.key]).toFixed(1) : '—'}
                  </td>
                ))}
                <td className="px-2 py-3 text-center text-xs font-bold text-slate-900 tabular-nums border-l border-slate-200">
                  {(parseFloat(bandTotals.saturday) || 0) > 0 ? parseFloat(bandTotals.saturday).toFixed(1) : '—'}
                </td>
                <td className="px-2 py-3 text-center text-xs font-bold text-slate-900 tabular-nums">
                  {(parseFloat(bandTotals.sunday) || 0) > 0 ? parseFloat(bandTotals.sunday).toFixed(1) : '—'}
                </td>
                <td className="px-2 py-3 text-center text-xs font-bold text-slate-900 tabular-nums">
                  {(parseFloat(bandTotals.public_holiday) || 0) > 0 ? parseFloat(bandTotals.public_holiday).toFixed(1) : '—'}
                </td>
                <td className="px-3 py-3 text-right font-bold text-slate-900 tabular-nums border-l border-slate-200 whitespace-nowrap">
                  {grandTotal.toFixed(1)} h
                </td>
                <td colSpan={3} />
                <td className="px-3 py-3 text-right text-xs font-bold text-slate-900 tabular-nums whitespace-nowrap">
                  {payTotal > 0 ? money(payTotal) : '—'}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        )}

        {/* Footer bar */}
        {!reportError && !loading && staffRows.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <p className="text-xs text-slate-600">
              Showing <span className="font-semibold">{staffRows.length}</span> staff member{staffRows.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
      </>}

      <PayLinesDialog ts={payLinesTs} open={!!payLinesTs} onClose={() => setPayLinesTs(null)}
        onChanged={() => {
          load();
          if (expandedStaff) {
            api.get(`/timesheets?staff_id=${expandedStaff}&start_date=${weekStart}&end_date=${periodEnd()}`)
              .then(r => setStaffTimesheets(r.data || [])).catch(() => {});
          }
        }} />
    </div>
  );
}
